import * as THREE from 'three';
import { createGameRng, getSeedFromUrl, generateSeed, cleanSeed } from './core/rng.js';
import {
  createBall, copyBall, placeBall, launchBall, puttBall, stepBall, SIM_DT, BALL_R, CUP_R, flatWorld,
} from './core/ballSim.js';
import { classify } from './core/happenings.js';
import { CARDS } from './core/cards.js';
import { pathLength, pathInfo } from './course/shapes.js';
import { buildCave, updateCave } from './render/cave.js';
import { makeRainCloud, updateRainCloud } from './render/gags.js';
// (a mouse: a PC, with a keyboard to aim with)
const FINE_POINTER = typeof matchMedia === 'function' && matchMedia('(pointer: fine)').matches;
import { eventFor, withEvent } from './core/events.js';
import { CLUBS, PUTTER, autoSelectClub, clubAllowed, distanceAt, lieFor, powerFor } from './core/clubs.js';
import {
  createSwing, startSwing, swingClick, swingStep, strikeFromTiming, describeStrike, markerToPercent, SWING,
} from './core/swing.js';
import {
  buildLaunch, powerForRest, powerForCarry, carryPoint, previewShot, previewPutt, puttSpeedFor, puttMeterMax, puttSpeedAt, slopeAlong,
} from './core/shotPlanner.js';
import { designHole, roundHoles, ARCHETYPES, ROUND_PLAN, GIMMICKS } from './course/holeDesigner.js';
import { buildWorld, aimTarget, teeSlot } from './course/courseWorld.js';
import { BIOMES } from './course/biomes.js';
import { buildTerrainMesh } from './render/terrainMesh.js';
import { buildScenery, updateScenery, updateFlag, disposeGroup, setSceneryViewport, buildRain } from './render/scenery.js';
import { Effects } from './render/effects.js';
import { Sky } from './render/sky.js';
import { CameraRig } from './render/cameraRig.js';
import { ClubRig, CAMPER_CLUB_SIZE, CAMPER_SCALE } from './render/club.js';
import {
  playerLevel, assistsFor, nextHeat, difficultyPips, caddieTip, challengeFor, newHoleLog,
} from './core/progression.js';
import { Hud, scoreName } from './ui/hud.js';
import { Fishing } from './ui/fishing.js';
import { CookHud } from './ui/cookHud.js';
import { Grill } from './render/grill.js';
import { Camper, GRIP } from './render/camper.js';
import { buildCampsite } from './render/campsite.js';
import { CamperUi } from './ui/camperUi.js';
import { loadCamp, bump } from './core/camp.js';
import { NetPlay } from './net/netplay.js';
import { buildNightGlow, updateNightGlow } from './render/nightGlow.js';
import { buildDressing } from './render/dressing.js';
import { Gimmicks } from './render/gimmicks.js';
import { Dog } from './play/dog.js';
import { Forage } from './play/forage.js';
import { Nabe } from './play/nabe.js';
import { Skip } from './play/skip.js';
import { pixelTexture } from './render/textures.js';
import { Audio } from './audio.js';
import { Music } from './music.js';

const SAVE_KEY = 'threewood.save.v2';
const BEST_KEY = 'threewood.best.v2';
const HINT_KEY = 'threewood.hints.v2';
const DAILY_KEY = 'threewood.daily.v1';
const CAMP_KEY = 'threewood.camp.v1';
const STEADY_KEY = 'threewood.steady.v1';

/** Everyone gets the same course each day. */
function dailySeed() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `DAILY-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const PUTT_METER_RATE = 82;   // meter units per second while holding PUTT
const INTRO_TIME = 4.6;

const store = {
  get(key) { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } },
  set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ } },
  del(key) { try { localStorage.removeItem(key); } catch { /* ignore */ } },
};

const feet = (yards) => Math.max(1, Math.round(yards * 3));

/**
 * The game: owns the scene, the round, and the per-shot state machine.
 *
 *   title -> intro -> aim -> swing -> flight -> (settle -> aim)* -> holed -> result -> intro ...
 */
export class Game {
  constructor(container) {
    this.container = container;
    this.coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.3, 1600);
    this.rig = new CameraRig(this.camera);
    this.initRenderer();
    this.initLights();
    this.sky = new Sky({ scene: this.scene, renderer: this.renderer, sun: this.sun, hemi: this.hemi, lowDetail: this.coarse });

    this.effects = new Effects(this.scene);
    this.clubRig = new ClubRig(this.scene);
    this.follow = null; // follow-through animation after a strike
    this.audio = new Audio();
    this.music = new Music(this.audio);
    this.hud = new Hud({
      onSwingDown: () => this.swingDown(),
      onSwingUp: () => this.swingUp(),
      onClub: (dir) => this.cycleClub(dir),
      onMenu: () => this.openMenu(),
      onStart: (choice) => this.startFromTitle(choice),
      onMusic: () => this.toggleMusic(),
    });
    this.fishing = new Fishing(this.hud.root, this.hud.layer);
    this.grill = new Grill(this.scene);
    // The player's camper, the things they have earned, and somewhere to sit
    this.camp = loadCamp(store.get(CAMP_KEY));
    this.camper = new Camper(this.camp.look);
    this.camper.group.visible = false;
    this.campsite = buildCampsite();
    this.campsite.visible = false;
    this.scene.add(this.camper.group, this.campsite);
    this.camperUi = new CamperUi(this.hud.root, this.hud.layer);
    this.newOutfits = [];
    this._pivot = new THREE.Vector3();
    this.net = new NetPlay(this);
    // Big moments (gold callouts) are shared with the room
    const callout = this.hud.callout.bind(this.hud);
    this.hud.callout = (text, kind = '') => { callout(text, kind); if (/gold/.test(kind) && !/^\+/.test(text)) this.net.brag(text); };
    this.photoCount = document.createElement('div');
    this.photoCount.className = 'photo-count hidden';
    document.body.appendChild(this.photoCount);
    this.gimmicks = new Gimmicks(this.scene);
    this.cookHud = new CookHud(this.hud.root, this.hud.layer, () => this.eatFish());
    // Things to do between shots: a dog, mushrooms to gather, stones to skip,
    // and a hot pot at the end of the round
    this.dog = new Dog(this);
    this.forage = new Forage(this);
    this.nabe = new Nabe(this);
    this.skip = new Skip(this);
    this.initBall();
    this.initInput();

    this.state = 'title';
    this.paused = false;
    this.time = 0;
    this.stateTime = 0;
    this.timeScale = 1;
    this.simAccumulator = 0;
    this.events = [];
    this.swing = createSwing();
    // Steady mode: for a jumpy bus. The meters pause where the tap belongs,
    // run slower, forgive more, and ignore the jitter of a bumped finger
    this.steady = !!store.get(STEADY_KEY);
    this.hints = { swing: 0, putt: 0, spin: 0, fish: 0, cook: 0, ...(store.get(HINT_KEY) || {}) };

    this.seedPinned = !!getSeedFromUrl();
    this.seed = getSeedFromUrl() || cleanSeed(store.get(SAVE_KEY)?.seed) || generateSeed();
    this.round = null;

    this.resize();
    window.addEventListener('resize', () => this.resize());
    window.addEventListener('orientationchange', () => setTimeout(() => this.resize(), 200));
    document.addEventListener('visibilitychange', () => {
      this.clock.getDelta();
      if (document.visibilityState !== 'visible') return;
      if (this.round) this.keepAwake();
      this.net.room?.wake();
      this.recover();
    });
    // A phone that was put away may hand back a fresh GL context
    this.canvas.addEventListener('webglcontextrestored', () => this.recover());

    this.showTitle();
    // Opened from a friend's link: straight to their room
    const room = new URLSearchParams(location.search).get('room');
    if (room) this.net.openLobby(room);
    this.clock = new THREE.Clock();
    this.frameTimes = [];
    this.loop = this.loop.bind(this);
    requestAnimationFrame(this.loop);

    window.THREEWOOD = this; // debug + automated play-testing hook
  }

  // ===========================================================================
  // Setup
  // ===========================================================================

  initRenderer() {
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(this.pixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = this.coarse ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
    this.container.appendChild(renderer.domElement);
    // A soft darkening at the edges of the frame, over the 3D, under the HUD
    const vignette = document.createElement('div');
    vignette.className = 'vignette';
    this.container.appendChild(vignette);
    this.fadeEl = document.createElement('div');
    this.fadeEl.className = 'fade';
    document.body.appendChild(this.fadeEl);
    this.renderer = renderer;
    this.canvas = renderer.domElement;
  }

  initLights() {
    this.sun = new THREE.DirectionalLight(0xffffff, 2.3);
    this.sun.castShadow = true;
    const size = this.coarse ? 1024 : 2048;
    this.sun.shadow.mapSize.set(size, size);
    const cam = this.sun.shadow.camera;
    cam.near = 20; cam.far = 700;
    cam.left = -95; cam.right = 95; cam.top = 95; cam.bottom = -95;
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.35;
    this.scene.add(this.sun, this.sun.target);
    this.hemi = new THREE.HemisphereLight(0xffffff, 0x88aa66, 1);
    this.scene.add(this.hemi);
    // Colours, direction and fog distances all belong to the sky
    this.scene.fog = new THREE.Fog(0xffffff, 150, 640);
  }

  initBall() {
    this.ball = createBall();
    // A proper ball: round, dimpled, and marked so you can see it spin
    const skin = document.createElement('canvas');
    skin.width = 256; skin.height = 128;
    const ctx = skin.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 256, 128);
    ctx.fillStyle = '#dfe3e6';
    for (let row = 0; row < 9; row++) {
      const v = (row + 0.5) / 9, count = Math.max(4, Math.round(22 * Math.sin(v * Math.PI)));
      for (let i = 0; i < count; i++) {
        ctx.beginPath();
        ctx.ellipse(((i + (row % 2) * 0.5) / count) * 256, v * 128, 3.4 / Math.max(0.35, Math.sin(v * Math.PI)), 3.4, 0, 0, 7);
        ctx.fill();
      }
    }
    ctx.fillStyle = '#ff4d4d';
    ctx.fillRect(96, 60, 64, 8);            // alignment stripe
    ctx.fillStyle = '#ffd23f';
    ctx.fillRect(220, 52, 14, 24);          // a second mark, so every spin axis shows
    const map = pixelTexture(skin, { colorSpace: THREE.SRGBColorSpace });
    this.ballMesh = new THREE.Mesh(
      new THREE.SphereGeometry(BALL_R, 24, 16),
      new THREE.MeshStandardMaterial({ map, roughness: 0.4, emissive: 0xffffff, emissiveMap: map, emissiveIntensity: 0.42 }));
    this.ballMesh.castShadow = true;
    this.scene.add(this.ballMesh);
    // A glow that travels with the ball at night
    this.ballLight = new THREE.PointLight(0xcfeeff, 0, 11, 1.4);
    this.scene.add(this.ballLight);
    // Blob shadow: the depth cue that makes ball flight readable
    this.blob = new THREE.Mesh(
      new THREE.CircleGeometry(1, 16).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.3, depthWrite: false }));
    this.blob.renderOrder = 2;
    this.scene.add(this.blob);
    this.ballScale = 1.4;
    this.sink = 0;
    // A soft halo in flight, so a ball against the sky reads at 200 yards
    const g = new Uint8Array(64 * 64 * 4);
    for (let y = 0; y < 64; y++) for (let x = 0; x < 64; x++) {
      const d = Math.hypot(x - 31.5, y - 31.5) / 31.5, a = Math.max(0, 1 - d) ** 2.2;
      g.set([255, 250, 230, Math.round(a * 255)], (y * 64 + x) * 4);
    }
    const glowTex = new THREE.DataTexture(g, 64, 64, THREE.RGBAFormat);
    glowTex.needsUpdate = true;
    this.ballGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, transparent: true, depthWrite: false, opacity: 0 }));
    this.ballGlow.renderOrder = 5;
    this.scene.add(this.ballGlow);
  }

  initInput() {
    const c = this.canvas;
    let drag = null;
    c.addEventListener('pointerdown', (e) => {
      this.audio.unlock();
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t: performance.now(), moved: 0, axis: null, len0: this.putting ? this.puttPct : this.wantDist };
      // A finger on Kuri's stick: this drag throws it, it does not turn the aim
      if (this.state === 'aim' && !this.paused && this.dog.grabStick(e.clientX, e.clientY)) drag.stick = true;
      try { c.setPointerCapture?.(e.pointerId); } catch { /* synthetic pointer */ }
    });
    c.addEventListener('pointermove', (e) => {
      if (!drag || drag.id !== e.pointerId) return;
      const dx = e.clientX - drag.x;
      const prevY = drag.y;
      drag.moved = Math.max(drag.moved, Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy));
      drag.x = e.clientX; drag.y = e.clientY;
      if (drag.stick) return;
      if (this.state === 'aim' && !this.paused) {
        // A drag is one thing or the other: sideways turns the aim, up and
        // down sets how far the shot goes. Decide on the first real movement.
        if (!drag.axis && drag.moved > 7) drag.axis = Math.abs(e.clientX - drag.sx) >= Math.abs(e.clientY - drag.sy) ? 'x' : 'y';
        if (drag.axis === 'y') { this.adjustLength(e.clientY - prevY); return; }
        if (drag.axis !== 'x') return;
        // Putts need a jeweller's touch; full shots a quicker turn
        const base = (this.putting ? 0.0011 : 0.003) * (this.steady ? 0.6 : 1);
        if (this.steady && Math.abs(dx) < 2) return; // a bump, not a drag
        this.aimAngle += dx * base * (420 / Math.max(320, Math.min(window.innerWidth, 900))) * (this.mods?.backwards ? -1 : 1); // (Backwards Day!)
        this.planDirty = true;
        if (drag.moved > 14) this.hud.demo(null); // they have got it
      } else if (this.state === 'flight' && !this.paused) {
        this.afterTouch(dx, e.clientY - prevY);
      } else if (this.state === 'creator') {
        // Turn the camper round to look at the back of the outfit
        this.camper.group.rotation.y += dx * 0.012;
      } else if (this.state === 'nabe') {
        this.nabe.stir(Math.hypot(dx, e.clientY - prevY) / Math.max(320, Math.min(window.innerWidth, 900)));
      } else if (this.state === 'cook') {
        this.grill.turn(dx / Math.max(320, Math.min(window.innerWidth, 900)));
        if (this.grill.spin > 0.3) this.cookHud.hideCue();
      }
    });
    const end = (e) => {
      if (!drag || drag.id !== e.pointerId) return;
      const held = performance.now() - drag.t;
      const tap = drag.moved < 12 && held < 400;
      const dy = e.clientY - drag.sy, dx = e.clientX - drag.sx;
      // A quick flick up or down: fly to a friend's ball, or come back.
      // (While aiming a vertical drag sets the length; only a fast flick with
      // a friend's ball in the air, or a flick back from watching, is a swipe)
      const quick = Math.abs(dy) > 70 && Math.abs(dy) > Math.abs(dx) * 1.6;
      const flick = quick && (this.state !== 'aim' || (held < 260 && (this.net.watching || this.net.flyingFriend())));
      if (flick && this.state === 'aim') { if (this.putting) this.puttPct = drag.len0; else this.wantDist = drag.len0; this.planDirty = true; this.previewUntil = 0; }
      const stick = drag.stick;
      drag = null;
      if (stick) { if (tap) this.dog.tapStick(e.clientX, e.clientY); else this.dog.flickStick(dx, dy, held); return; }
      if (this.state === 'skip') { if (!tap) this.skip.flick(dx, dy, held); return; }
      if (tap) this.tap(e.clientX, e.clientY);
      else if (flick && this.state !== 'flight') this.net.swipe(dy < 0);
    };
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', end);
    // A mouse wheel does the same as the vertical drag (PC)
    c.addEventListener('wheel', (e) => {
      if (this.state !== 'aim' || this.paused) return;
      e.preventDefault();
      this.adjustLength(Math.sign(e.deltaY) * 36);
    }, { passive: false });
    for (const type of ['pointerdown', 'touchend', 'click']) {
      document.addEventListener(type, () => { this.audio.unlock(); this.music.wake(); }, { capture: true, passive: true });
    }

    this.keys = {};
    window.addEventListener('keydown', (e) => {
      // (typing a room code is not aiming)
      if (e.target?.closest?.('input, textarea')) return;
      if (['KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code) && this.state === 'aim') e.preventDefault();
      if (e.repeat) return;
      this.keys[e.code] = true;
      this.audio.unlock();
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        if (this.state === 'intro') this.tap(); else this.swingDown();
      } else if (e.code === 'ArrowUp') this.cycleClub(-1);
      else if (e.code === 'ArrowDown') this.cycleClub(1);
      else if (e.code === 'Escape') { if (this.paused) this.closeMenu(); else this.openMenu(); }
    });
    // (a key let go in another window must not keep the aim turning)
    window.addEventListener('blur', () => { for (const k of Object.keys(this.keys)) this.keys[k] = false; });
    window.addEventListener('keyup', (e) => {
      this.keys[e.code] = false;
      if (e.code === 'Space' || e.code === 'Enter') this.swingUp();
    });
  }

  /**
   * Compile every shader the hole will need now, while the hole card is up,
   * rather than the first time something appears mid-shot (a friend's club,
   * Kuri, a mushroom): each first appearance used to cost a visible hitch.
   */
  precompile() {
    const hidden = [];
    const show = (o) => { if (o && !o.visible) { o.visible = true; hidden.push(o); } };
    for (const o of this.net.others.map.values()) { show(o.camper.group); show(o.club.group); for (const c of o.club.group.children) c.traverse(show); show(o.ball); }
    show(this.dog.group); show(this.clubRig.group); show(this.camper.group);
    // A real draw, not just a compile: that also uploads every mesh's buffers.
    // (It is drawn over at once; the hole card hides it anyway.)
    try { this.renderer.compile(this.scene, this.camera); this.renderer.render(this.scene, this.camera); } catch { /* best effort */ }
    for (const o of hidden) o.visible = false;
  }

  /** Title screen: the free strip of screen between the logo and the menu (px). */
  titleBand() {
    const logo = this.hud.layer.querySelector('.title .logo')?.getBoundingClientRect();
    const first = this.hud.layer.querySelector('.title-actions .btn')?.getBoundingClientRect();
    const H = window.innerHeight;
    const top = logo ? logo.bottom + 6 : H * 0.2, bottom = first ? first.top - 10 : H * 0.6;
    return { h: bottom - top, mid: (top + bottom) / 2 };
  }

  resize() {
    const w = window.innerWidth, h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.rig.setAspect(w / h);
    this.effects.resize(w, h, this.pixelRatio);
    setSceneryViewport(h * this.pixelRatio);
  }

  // ===========================================================================
  // Title / round lifecycle
  // ===========================================================================

  showTitle() {
    this.setState('title');
    this.round = null;
    this.hud.setPlayVisible(false);
    this.loadWorld(designHole(this.seed, 1));
    this.ballMesh.visible = this.blob.visible = false;
    // Where the camper stands for the title shot: on the green, short of the flag
    {
      const { cup, tee } = this.world;
      const d = Math.hypot(tee.x - cup.x, tee.z - cup.z) || 1;
      const x = cup.x + ((tee.x - cup.x) / d) * 5.5, z = cup.z + ((tee.z - cup.z) / d) * 5.5;
      this.titleSpot = { x, z, y: this.world.heightAt(x, z), angle: Math.atan2(tee.z - cup.z, tee.x - cup.x) + 0.5 };
    }
    const saved = store.get(SAVE_KEY);
    const usable = saved && !saved.net && cleanSeed(saved.seed) && saved.index < saved.holes.length;
    this.hud.showTitle({
      saved: usable ? { hole: saved.index + 1, total: saved.scores.reduce((s, h) => s + h.strokes - h.par, 0) - 2 * (saved.gold || 0) } : null,
      best: store.get(BEST_KEY),
      daily: store.get(DAILY_KEY)?.[dailySeed()] ?? null,
      seed: this.seed,
      music: this.music.enabled,
      sky: this.sky.describe(),
    });
    this.rig.cut();
  }

  startFromTitle(choice) {
    this.audio.unlock();
    this.audio.tap();
    if (choice === 'fishing') { this.practiceFishing(() => this.showTitle()); return; }
    if (choice === 'grill') { this.startCooking(() => this.showTitle()); return; }
    if (choice === 'camper') { this.openCreator(); return; }
    if (choice === 'friends') { this.net.openLobby(); return; }
    const saved = store.get(SAVE_KEY);
    if (choice === 'continue' && saved && cleanSeed(saved.seed)) {
      this.seed = saved.seed;
      this.round = saved;
    } else {
      if (choice === 'daily') {
        this.seed = dailySeed();
      } else if (!this.seedPinned && saved && saved.seed === this.seed) {
        // Abandoning a round: grow a new course rather than replaying it
        this.seed = generateSeed();
      }
      const length = choice === 'daily' ? 18 : Number(choice);
      this.round = {
        daily: choice === 'daily',
        seed: this.seed,
        length,
        holes: roundHoles(length),
        index: 0,
        scores: [],
        points: 0,
        pureStreak: 0,
        stats: { fairways: 0, fairwayChances: 0, gir: 0, putts: 0, longestDrive: 0, longestPutt: 0, pures: 0, swings: 0 },
      };
      store.set(SAVE_KEY, this.round);
    }
    this.rng = createGameRng(`${this.seed}:play:${Date.now()}`).rng;
    this.hud.clearLayer();
    this.keepAwake();
    this.dip(() => this.startHole());
  }

  /**
   * A dip through dark green: fade out, do the cut (a new hole, a new scene)
   * while nothing can be seen, fade back in. Instant in manual (test) mode.
   */
  dip(cut) {
    if (this.manual || !this.fadeEl) { cut(); return; }
    this.fadeEl.classList.add('on');
    clearTimeout(this.dipTimer);
    this.dipTimer = setTimeout(() => {
      cut();
      this.rig.cut();
      // Two frames for the new scene to draw before it is revealed
      requestAnimationFrame(() => requestAnimationFrame(() => this.fadeEl.classList.remove('on')));
    }, 260);
  }

  /** A round with friends: everyone builds the same course from the host's seed. */
  startNetRound(seed, length) {
    this.audio.unlock();
    this.seed = seed;
    this.round = {
      net: true, daily: false, seed, length,
      holes: roundHoles(length), index: 0, scores: [], points: 0, pureStreak: 0,
      stats: { fairways: 0, fairwayChances: 0, gir: 0, putts: 0, longestDrive: 0, longestPutt: 0, pures: 0, swings: 0 },
    };
    this.rng = createGameRng(`${seed}:play:${Date.now()}`).rng;
    this.hud.clearLayer();
    this.keepAwake();
    this.startHole();
  }

  /** NEXT on the result card: with friends, the room moves on together. */
  pressNext() {
    if (this.net.active) this.net.pressNext(); else this.nextHole?.();
  }

  /**
   * Back from the background: anything the GPU may have dropped is marked for
   * re-upload, and the shadow map is rebuilt rather than trusted.
   */
  recover() {
    this.scene.traverse((o) => {
      if (!o.isMesh) return;
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        if (!m) continue;
        m.needsUpdate = true;
        for (const key of ['map', 'emissiveMap', 'gradientMap', 'alphaMap', 'normalMap']) if (m[key]) m[key].needsUpdate = true;
      }
    });
    if (this.sun.shadow.map) { this.sun.shadow.map.dispose(); this.sun.shadow.map = null; }
    this.renderer.shadowMap.needsUpdate = true;
  }

  /** Keep the screen on during a round (it is a long time between taps on a putt read). */
  async keepAwake() {
    try {
      this.wakeLock = await navigator.wakeLock?.request('screen');
    } catch { /* not supported or denied: fine */ }
  }

  /** Swap in a new hole's world, mesh and scenery. */
  loadWorld(spec) {
    if (this.terrain) {
      this.scene.remove(this.terrain, this.scenery.group);
      disposeGroup(this.terrain);
      disposeGroup(this.scenery.group);
    }
    this.world = buildWorld(spec);
    this.terrain = buildTerrainMesh(this.world);
    this.scenery = buildScenery(this.world, { lowDetail: this.coarse });
    this.scene.add(this.terrain, this.scenery.group);
    if (this.nightGlow) { this.scene.remove(this.nightGlow); disposeGroup(this.nightGlow); }
    this.nightGlow = buildNightGlow(this.world);
    this.scene.add(this.nightGlow);
    if (this.cave) { this.scene.remove(this.cave); disposeGroup(this.cave); this.cave = null; }
    if (this.world.ceilingAt) { this.cave = buildCave(this.world); this.scene.add(this.cave); }
    if (this.dressing) { this.scene.remove(this.dressing); disposeGroup(this.dressing); }
    this.dressing = buildDressing(this.world);
    this.scene.add(this.dressing);
    this.gimmicks?.load(this.world);
    this.sky.setHole(this.world);
    this.rig.setWorld(this.world, this.scenery.placed);
    this.effects.hideAim();
    this.effects.beads.hide();
    this.effects.clearTrail();
    this.env = { windX: spec.wind.x, windZ: spec.wind.z };
  }

  startHole() {
    const round = this.round;
    const number = round.holes[round.index];
    // The crown passes first, and the cards are played: they can shape the
    // hole (a wider cup, a breeze) for me alone
    this.net.passCrown();
    // What was bought from Tabi after the last hole comes into play now
    if (round.shopFor !== round.index) { round.shopNow = round.shopNext || []; round.shopNext = []; round.shopFor = round.index; }
    const mods = this.net.holeMods();
    let spec = designHole(round.seed, number);
    // A room event shapes the hole for everyone (the same one on every phone)
    this.roomEvent = this.net.active && this.net.players.length > 1 ? eventFor(round.seed, round.index, spec) : null;
    spec = withEvent(spec, this.roomEvent);
    if (mods.cup !== 1) spec = { ...spec, cupR: (spec.cupR || CUP_R) * mods.cup };
    if (mods.gravity !== 1) spec = { ...spec, gravity: (spec.gravity || 9.81) * mods.gravity };
    if (mods.bouncy !== 1) spec = { ...spec, bouncy: mods.bouncy };
    this.loadWorld(spec);
    this.mods = mods;
    this.mulligans = mods.mulligan || 0;
    this.lastFrom = null;
    if (mods.slick !== 1) this.world.slick = mods.slick;
    this.gags(mods);
    if (mods.wind) {
      // Along the hole, tee to pin, on top of the day's wind (+ at my back)
      const dx = spec.pin.x - spec.tee.x, dz = spec.pin.z - spec.tee.z, d = Math.hypot(dx, dz) || 1;
      this.env = { windX: this.env.windX + (dx / d) * mods.wind, windZ: this.env.windZ + (dz / d) * mods.wind };
    }

    this.strokes = 0;
    this.putts = 0;
    this.bonuses = [];
    this.holePoints = 0;
    // With friends, everyone tees off from their own seat's spot on the tee
    const seat = this.net.active ? this.net.seatOf(this.net.myId) : 0;
    const teeAt = seat ? teeSlot(this.world, seat) : this.world.tee;
    placeBall(this.ball, this.world, teeAt.x, teeAt.z);
    this.ballMesh.visible = this.blob.visible = true;
    this.sink = 0;

    this.hud.setPlayVisible(true);
    this.hud.setControlsVisible(false);
    this.hud.setHole({ index: round.index + 1, total: round.holes.length, par: spec.par, yards: spec.length });
    this.hud.setPoints(round.points);
    this.updateScoreHud();
    // Where this hole sits on the ramp: what help is left, and what it asks
    this.level = playerLevel(round.index, round.holes.length, round.heat || 0);
    this.assists = assistsFor(this.level);
    const seenOfPar = round.scores.filter((h) => h.par === spec.par).length;
    const seenOfKind = round.holes.slice(0, round.index).filter((n) => ROUND_PLAN[n - 1].archetypes.includes(spec.archetype)).length;
    this.challenge = challengeFor(spec.par, seenOfPar, spec.archetype, seenOfKind);
    this.holeLog = newHoleLog(spec.par);
    this.hud.setChallenge(this.challenge.text, null);
    this.net.atHole(); // (before the card: it names the hole's contest)
    this.hud.showIntro(this.introCard());
    this.precompile();
    this.forage.atHole();
    this.dog.atHole();
    this.landing = null;
    this.aimAngle = Math.atan2(this.world.cup.z - this.ball.z, this.world.cup.x - this.ball.x);
    this.hud.drawMinimap(this.world, this.ball, null);

    // Resuming mid-hole (the tab was closed on the 14th fairway): pick up
    // exactly where the ball was lying.
    const live = round.live;
    if (live && live.index === round.index && live.strokes > 0) {
      this.strokes = live.strokes;
      this.putts = live.putts;
      this.bonuses = live.bonuses || [];
      this.holePoints = live.holePoints || 0;
      if (live.holeLog) this.holeLog = live.holeLog;
      placeBall(this.ball, this.world, live.x, live.z);
      this.hud.clearLayer();
      this.beginAim();
      return;
    }
    this.setState('intro');
    this.rig.flyover(0);
    this.rig.cut();
    this.audio.whoosh();
  }

  introCard() {
    const { round } = this, spec = this.world.spec, arch = ARCHETYPES[spec.archetype];
    return {
      index: round.index + 1, total: round.holes.length,
      name: arch.label, blurb: arch.blurb, par: spec.par, yards: spec.length,
      biome: BIOMES[spec.biome].name,
      pips: difficultyPips(spec.difficulty ?? 0, this.level),
      wind: spec.wind.speed,
      tip: caddieTip(round.index, this.level),
      challenge: this.challenge,
      toy: spec.gimmick ? GIMMICKS[spec.gimmick.kind] : null,
      pond: spec.fishing ? this.pondSide(spec.fishing) : null,
      crown: this.net.crownCard(),
      bought: (round.shopNow || []).map((id) => ({ ...CARDS[id], mine: true })),
      event: this.roomEvent,
    };
  }

  /** Which side of the first tee shot a pond sits, as the player sees it. */
  pondSide(pond) {
    const { tee, cup } = this.world;
    const dx = cup.x - tee.x, dz = cup.z - tee.z;
    const cross = dx * (pond.z - tee.z) - dz * (pond.x - tee.x);
    return cross > 0 ? 'right' : 'left';
  }

  roundTotal() {
    return this.round.scores.reduce((s, h) => s + h.strokes - h.par, 0) - 2 * (this.round.gold || 0);
  }

  updateScoreHud(label) {
    this.hud.setScore({ strokes: this.strokes, total: this.roundTotal(), shotLabel: label });
  }

  cardData() {
    const round = this.round;
    return {
      currentLabel: round.holes[round.index],
      holes: round.holes.map((n, i) => ({
        label: n,
        par: ROUND_PLAN[n - 1].par,
        strokes: round.scores[i]?.strokes ?? null,
        current: i === round.index,
      })),
    };
  }

  setState(state) {
    // (while lining up and swinging, the screen keeps out of the way of the shot: see styles)
    this.hud.root.classList.toggle('aiming', state === 'aim' || state === 'swing');
    // (the 'tap to speed it up' hint belongs to a flight only)
    if (this.state === 'flight' && state !== 'flight' && this.shot?.ffHinted) this.hud.hint('');
    if (state !== 'fishing' && this.fishing?.active) this.fishing.stop();
    const atFire = state === 'summary' || state === 'nabe';
    if (state !== 'cook' && !atFire && this.grill?.active) { this.grill.stop(); this.cookHud.hide(); }
    if (!atFire && this.campsite) { this.campsite.visible = false; this.camperUi.hidePoses(); }
    if (!atFire && this.nabe?.active) this.nabe.stop();
    // The title's framing slides the picture; nothing else does
    if (state !== 'title' && this.camera.view) { this.camera.clearViewOffset(); this.titleShift = 0; }
    this.state = state;
    this.stateTime = 0;
    this.music.setScene(state);
    // The flyover is the hole's title sequence: no play HUD over it
    this.hud.root.classList.toggle('cinematic', state === 'intro');
  }

  // ===========================================================================
  // Aiming
  // ===========================================================================

  get putting() { return this.club === PUTTER; }

  clubKind() {
    if (this.putting) return 'putter';
    return this.club.id === 'driver' || this.club.id === 'wood3' ? 'wood' : 'iron';
  }

  /** Swing angle for the club model, read straight off the meter. */
  updateClub(dt) {
    const { ball } = this;
    // Drawn large enough to read from the tee camera
    const camDist = Math.hypot(this.camera.position.x - ball.x, this.camera.position.y - ball.y, this.camera.position.z - ball.z);
    // (smaller than it once was: there is a camper holding it now, and they
    // have to fit on a phone screen beside the ball)
    // One size for every golfer on the course (see CAMPER_CLUB_SIZE)
    const size = CAMPER_CLUB_SIZE;
    void camDist;
    if (this.follow) {
      // Through the ball and up, then fade away
      const f = this.follow;
      f.t += dt;
      const k = Math.min(1, f.t / 0.26);
      const ease = 1 - (1 - k) * (1 - k);
      const theta = (f.putt ? 0.55 : 2.5) * ease;
      // Hold the finish a moment before the club goes
      const fade = Math.max(0, 1 - Math.max(0, f.t - 1.1) / 0.35);
      this.clubRig.pose(f, f.dirX, f.dirZ, theta, f.kind, f.size, fade, this.camper.group.visible ? this.camper.shoulderWorld : null);
      if (fade <= 0) this.follow = null;
      return;
    }
    if (!this.round || (this.state !== 'aim' && this.state !== 'swing')) { this.clubRig.hide(); return; }
    const dirX = Math.cos(this.aimAngle), dirZ = Math.sin(this.aimAngle);
    let theta = Math.sin(this.time * 2.2) * 0.035; // waggle at address
    if (this.state === 'swing') {
      if (this.putting) {
        theta = -0.6 * (this.puttPower / 100);
      } else {
        const sw = this.swing;
        const reach = this.club.carry < 40 ? 1.3 : 2.7;
        const top = (p) => -reach * (0.3 + 0.7 * p / 100);
        if (sw.phase === 'power') {
          const p = sw.marker / 100;
          theta = -reach * (0.3 * Math.min(1, p * 4) + 0.7 * p);
        } else if (sw.phase === 'accuracy') {
          // Down from the top: the head reaches the ball as the marker
          // reaches the line (and swings past it if you are late)
          const span = Math.max(1, sw.power - SWING.LINE);
          const k = (sw.marker - SWING.LINE) / span;
          theta = top(sw.power) * (k >= 0 ? k * k : k * 0.6);
        }
      }
    }
    this.clubSize = size;
    this.clubTheta = theta;
    // The club arrives with the camper: nothing floats at the ball while they walk up
    if (this.walkIn) { this.clubRig.hide(); return; }
    this.clubRig.pose(ball, dirX, dirZ, theta, this.clubKind(), size, 1, this.camper.group.visible && this.camper.shoulderWorld?.lengthSq() ? this.camper.shoulderWorld : null);
  }

  // ===========================================================================
  // The camper
  // ===========================================================================

  /** Count something toward the outfits; announce any that it earns. */
  earn(stat, by = 1) {
    const got = bump(this.camp, stat, by);
    store.set(CAMP_KEY, this.camp);
    for (const fit of got) {
      this.newOutfits.push(fit.name);
      this.hud.callout(`NEW OUTFIT · ${fit.name.toUpperCase()}`, 'gold small keep'); // (never cleared early)
      this.audio.reward(6);
    }
  }

  /** Where the camper stands to hold the club at the ball. */
  addressSpot() {
    const { ball, world } = this;
    const dirX = Math.cos(this.aimAngle), dirZ = Math.sin(this.aimAngle);
    const p = ClubRig.pivot(ball, dirX, dirZ, this.clubKind(), this.clubSize || 1.3, this._pivot);
    // The golfer's side of the ball, facing it
    const nx = dirZ, nz = -dirX;
    let scale = (p.y - world.heightAt(p.x, p.z)) / GRIP.y, x = p.x, z = p.z, y = 0;
    for (let i = 0; i < 2; i++) {
      x = p.x + nx * GRIP.z * scale; z = p.z + nz * GRIP.z * scale;
      y = world.heightAt(x, z);
      scale = Math.max(1.2, Math.min(7, (p.y - y) / GRIP.y));
    }
    return { x, y, z, scale, yaw: Math.atan2(-nx, -nz), dirX, dirZ, nx, nz };
  }

  /** Stand a camper at a point given in another object's local space. */
  placeCamper(parent, lx, lz, yaw, scale, lift = 0) {
    const g = this.camper.group;
    parent.updateMatrixWorld();
    const v = this._pivot.set(lx, 0, lz).applyMatrix4(parent.matrixWorld);
    g.position.set(v.x, this.world.heightAt(v.x, v.z) + lift, v.z);
    g.rotation.y = yaw + parent.rotation.y;
    g.scale.setScalar(scale);
  }

  updateCamper(dt) {
    const c = this.camper, g = c.group, { world, ball } = this;
    const s = this.state;
    let pose = 'idle', twist = 0, show = true;
    if (s === 'creator') {
      pose = 'idle';
    } else if (s === 'cook' || s === 'summary' || s === 'nabe') {
      pose = this.campPose || 'warm';
    } else if (s === 'skip') {
      // On the bank, facing the water; the arm goes up for a throw
      const sh = this.skip.shore;
      const x = sh.x - sh.dx * 0.4 + sh.dz * 0.6, z = sh.z - sh.dz * 0.4 - sh.dx * 0.6;
      const dx = x - g.position.x, dz = z - g.position.z, d = Math.hypot(dx, dz);
      if (d > 0.2) {
        const step = Math.min(d, Math.max(6, d * 1.5) * dt);
        g.position.set(g.position.x + (dx / d) * step, 0, g.position.z + (dz / d) * step);
        g.position.y = Math.max(world.heightAt(g.position.x, g.position.z), sh.pond.level);
        g.rotation.y = Math.atan2(dx, dz);
        pose = 'walk';
      } else {
        g.rotation.y = Math.atan2(sh.dx, sh.dz);
        pose = this.skip.throwT > 0 ? 'wave' : 'idle';
      }
      g.scale.setScalar(CAMPER_SCALE);
    } else if (s === 'title' && this.titleSpot) {
      // On the title screen: on the green, waving now and then at the camera
      const t = this.titleSpot;
      g.position.set(t.x, t.y, t.z);
      g.scale.setScalar(CAMPER_SCALE);
      g.rotation.y = Math.atan2(this.camera.position.x - t.x, this.camera.position.z - t.z);
      pose = (this.time % 7) < 2.2 ? 'wave' : 'idle';
    } else if (!this.round || s === 'title' || s === 'intro' || s === 'fishing') {
      show = false;
    } else if (s === 'aim' || s === 'swing') {
      const spot = this.addressSpot();
      const w = this.walkIn;
      if (w && s === 'aim' && w.t < w.dur) {
        if (w.x === undefined) {
          // Walk in from behind the ball, past the camera
          w.x = spot.x - spot.dirX * 2.6 * spot.scale + spot.nx * 0.4 * spot.scale;
          w.z = spot.z - spot.dirZ * 2.6 * spot.scale + spot.nz * 0.4 * spot.scale;
        }
        w.t += dt;
        const k = Math.min(1, w.t / w.dur);
        const x = w.x + (spot.x - w.x) * k, z = w.z + (spot.z - w.z) * k;
        g.position.set(x, world.heightAt(x, z), z);
        g.rotation.y = Math.atan2(spot.x - w.x, spot.z - w.z);
        g.scale.setScalar(spot.scale);
        pose = 'walk';
      } else {
        this.walkIn = null;
        g.position.set(spot.x, spot.y, spot.z);
        g.rotation.y = spot.yaw;
        g.scale.setScalar(spot.scale);
        pose = 'address';
        twist = Math.max(-1, Math.min(1, (this.clubTheta || 0) / 2.2));
      }
    } else if (s === 'flight' || s === 'settle') {
      // Hold the finish, then watch it go
      if (this.follow) { pose = 'address'; twist = 1; }
    } else if (s === 'holed' || s === 'result') {
      if (!this.cheerSpot) {
        // Beyond the cup from where the camera starts, a step to the side: the
        // flag, the cup and the camper share the frame and she never blocks them
        const a = (this.celebrateAngle || 0) + Math.PI + 0.42;
        const x = world.cup.x + Math.cos(a) * 2.2, z = world.cup.z + Math.sin(a) * 2.2;
        this.cheerSpot = { x, z };
        g.position.set(x, world.heightAt(x, z), z);
        g.scale.setScalar(CAMPER_SCALE);
      }
      g.rotation.y = Math.atan2(this.camera.position.x - g.position.x, this.camera.position.z - g.position.z);
      pose = this.strokes <= world.spec.par ? 'cheer' : 'wave';
    }
    g.visible = show;
    const swinging = pose === 'address' && this.clubRig.group.visible;
    if (show) c.update(dt, pose, { twist, arms: swinging ? this.clubRig : null });
    // The chase camera passes close by: get out of its way rather than fill
    // the lens with a hat
    if (show && s === 'flight') {
      const head = this._pivot.set(g.position.x, g.position.y + g.scale.y * 0.9, g.position.z);
      // (the putting camera walks in right past her: a wider margin there)
      const margin = (this.shot?.putt ? 3.6 : 2.6) * (g.scale.y / 1.9);
      if (this.camera.position.distanceTo(head) < margin) { g.visible = false; this.clubRig.hide(); this.follow = null; }
    }
  }

  /**
   * The creator's camera: the whole camper, feet to hair, in the space ABOVE
   * the panel (on an upright phone the panel is most of the screen). The
   * panel's top is measured now and then, and the camera worked out to fit.
   */
  frameCreator(dt) {
    const v = this.creatorView;
    this.creatorMeasure = (this.creatorMeasure || 0) - dt;
    if (this.creatorMeasure <= 0 || this.creatorTop == null) {
      this.creatorMeasure = 0.4;
      const panel = document.querySelector('.camper-ui .panel');
      const r = panel ? panel.getBoundingClientRect() : null;
      // Beside the panel (landscape: it runs down the right) or above it
      this.creatorSide = !!r && r.left > window.innerWidth * 0.3;
      this.creatorLeft = r ? r.left : window.innerWidth;
      const top = r && !this.creatorSide ? r.top : window.innerHeight;
      this.creatorTop = Math.max(window.innerHeight * 0.18, Math.min(window.innerHeight, top));
    }
    // The band to fill, in screen units (-1 bottom .. 1 top), with a little room
    const hi = 1 - 2 * (Math.max(this.creatorSide ? 30 : 70, window.innerHeight * 0.09) / window.innerHeight);
    const lo = 1 - 2 * (this.creatorTop / window.innerHeight) + 0.06;
    // ...and across: the middle of the space left of the panel
    const wantX = this.creatorSide ? (-1 + (2 * this.creatorLeft / window.innerWidth - 1)) / 2 : 0;
    const s = this.camper.group.scale.x;
    const feet = v.y, head = v.y + 1.08 * s;
    let r = this.creatorR || 2.6, lookY = this.creatorLookY ?? v.y + 0.45, side = this.creatorShift || 0;
    const tanHalf = Math.tan((this.camera.fov * Math.PI) / 360);
    // (sideways: the camera's right, as it looks in at the camper)
    const rx = Math.sin(v.angle), rz = -Math.cos(v.angle);
    // (beside a landscape panel the camper is off to one side of the picture:
    // a level camera there, or looking down at them they lean like a tower)
    const place = () => this.rig.orbit(v.x + rx * side, lookY - 0.3, v.z + rz * side, r, this.creatorSide ? 0.3 : (lookY - v.y) + 0.35, v.angle);
    for (let i = 0; i < 6; i++) {
      place();
      const a = this.rig.ndc(v.x, feet, v.z), ay = a.y, ax = a.x, b = this.rig.ndc(v.x, head, v.z).y;
      const span = b - ay, want = (hi - lo) * (this.creatorSide ? 0.72 : 0.92);
      if (span > 0.01) r = Math.max(1.6, Math.min(14, r * span / want));
      const mid = (ay + b) / 2, wantMid = (hi + lo) / 2;
      lookY += (mid - wantMid) * tanHalf * r;
      side += (ax - wantX) * tanHalf * this.camera.aspect * r;
    }
    this.creatorR = r; this.creatorLookY = lookY; this.creatorShift = side;
    place();
  }

  /** The camper creator: build your golfer, try on what you have earned. */
  openCreator() {
    const { world } = this;
    const spot = Grill.place(world, this.scenery.placed);
    const y = world.heightAt(spot.x, spot.z);
    this.creatorView = { x: spot.x, y, z: spot.z, angle: Math.atan2(world.cup.z - spot.z, world.cup.x - spot.x) };
    this.creatorR = null; this.creatorLookY = null; this.creatorTop = null; this.creatorShift = 0;
    this.hud.clearLayer();
    this.setState('creator');
    const g = this.camper.group;
    g.position.set(spot.x, y, spot.z);
    g.scale.setScalar(1.75);
    g.rotation.y = Math.atan2(Math.cos(this.creatorView.angle), Math.sin(this.creatorView.angle));
    this.camper.setLook(this.camp.look);
    this.camperUi.open(this.camp, (look) => this.camper.setLook(look), (look) => {
      this.camp.look = look;
      this.camp.made = true;
      store.set(CAMP_KEY, this.camp);
      this.camper.setLook(look);
      this.showTitle();
    });
  }

  /** The end of the round: pitch camp by the last green and sit by the fire. */
  pitchCamp() {
    const { world } = this;
    const spot = Grill.place(world, this.scenery.placed);
    const y = world.heightAt(spot.x, spot.z);
    const view = Math.atan2(world.cup.z - spot.z, world.cup.x - spot.x);
    const facing = Math.atan2(-Math.cos(view), -Math.sin(view));
    this.campView = { x: spot.x, y, z: spot.z, angle: view };
    this.grill.start({ x: spot.x, y, z: spot.z, facing, fish: false });
    this.campsite.position.set(spot.x, y, spot.z);
    this.campsite.rotation.y = facing + Math.PI; // its front faces the camera
    this.campsite.visible = true;
    this.setCampPose('sit');
    this.net.campfire(this.campsite);
  }

  /** The pose bar by the fire, with the camera. back() returns to the summary card. */
  campPoses(back) {
    this.hud.clearLayer();
    this.camperUi.showPoses((pose) => this.setCampPose(pose), () => { this.setCampPose('sit'); back(); }, () => this.takePhoto(() => this.campPoses(back)));
  }

  /**
   * A camp photo: the HUD goes away, 3-2-1, and the frame is saved to share.
   * then() brings the pose bar back afterwards.
   */
  takePhoto(then) {
    this.photo = { t: 0, then };
    this.hud.root.classList.add('photo');
    this.photoCount.classList.remove('hidden');
    this.audio.tap();
  }

  /**
   * The camp photo's camera: a little wider and higher than the fireside view,
   * stepped back until everyone round the fire (and Kuri) is in the picture,
   * feet to heads, whatever the screen's shape (an upright phone is narrow).
   */
  photoFrameCamp(v) {
    const pts = [this.camper.group.position];
    for (const o of this.net.others.map.values()) if (o.camp && o.camper.group.visible) pts.push(o.camper.group.position);
    if (this.dog?.group?.visible) pts.push(this.dog.group.position);
    let cx = 0, cz = 0;
    for (const p of pts) { cx += p.x; cz += p.z; }
    cx = (cx / pts.length + v.x) / 2; cz = (cz / pts.length + v.z) / 2;
    let r = 5.2;
    for (let i = 0; i < 12; i++) {
      this.rig.orbit(cx, v.y + 0.85, cz, r, 1.5 + (r - 5.2) * 0.25, v.angle - 0.3);
      let fits = true;
      for (const p of pts) {
        for (const up of [0.05, 2.0]) {
          const n = this.rig.ndc(p.x, p.y + up, p.z);
          if (n.z >= 1 || Math.abs(n.x) > 0.9 || n.y < -0.9 || n.y > 0.85) { fits = false; break; }
        }
        if (!fits) break;
      }
      if (fits) break;
      r *= 1.12;
    }
  }

  /** Runs after the frame is drawn: counts down, then grabs the canvas. */
  photoFrame(dt) {
    const p = this.photo;
    if (!p) return;
    p.t += dt;
    const left = Math.ceil(3 - p.t);
    if (left >= 1) { this.photoCount.textContent = left; return; }
    this.photoCount.textContent = '';
    if (p.t < 3.15) return; // one clean frame with no number on it
    this.photo = null;
    this.photoCount.classList.add('hidden');
    this.hud.root.classList.remove('photo');
    this.audio.reward(3);
    this.canvas.toBlob((blob) => {
      if (!blob) { p.then(); return; }
      const url = URL.createObjectURL(blob);
      const file = new File([blob], 'threewood-camp.png', { type: 'image/png' });
      const canShare = !!navigator.canShare?.({ files: [file] });
      this.hud.clearLayer();
      const node = document.createElement('div');
      node.className = 'overlay dim';
      node.innerHTML = `<div class="card photo-card"><img alt="Camp photo"><div class="btn-row">${canShare ? '<button class="btn" data-a="share">SHARE</button>' : ''}<a class="btn ${canShare ? 'ghost' : ''}" data-a="save" download="threewood-camp.png">SAVE</a></div><button class="btn ghost" data-a="close">BACK</button></div>`;
      node.querySelector('img').src = url;
      node.querySelector('[data-a="save"]').href = url;
      node.addEventListener('click', async (e) => {
        const a = e.target.closest('[data-a]')?.dataset.a;
        if (a === 'share') { try { await navigator.share({ files: [file], title: 'ThreeWood' }); } catch { /* cancelled */ } }
        if (a === 'close') { URL.revokeObjectURL(url); this.hud.clearLayer(); p.then(); }
      });
      this.hud.layer.appendChild(node);
    }, 'image/png');
  }

  setCampPose(pose) {
    const seated = pose === 'sit' || pose === 'warm';
    // On the log they perch, knees bent, rather than sit flat on the ground
    this.campPose = pose === 'sit' ? 'perch' : pose === 'warm' ? 'perchWarm' : pose;
    this.net.setPose(pose); // friends see it (they have no log: they sit on the ground)
    const at = this.campsite.userData[seated ? 'seat' : 'stand'];
    const scale = 1.75;
    this.placeCamper(this.campsite, at.x, at.z, at.yaw, scale, seated ? at.y - 0.45 * scale : 0);
  }

  beginAim() {
    const { ball, world } = this;
    placeBall(ball, world, ball.x, ball.z);
    this.sink = 0;
    this.timeScale = 1;
    this.fastForward = false;
    this.onTee = this.strokes === 0;
    this.lie = this.perkLie(lieFor(ball.surface));

    const toPin = Math.hypot(world.cup.x - ball.x, world.cup.z - ball.z);
    // (crazy golf is all green, but the line is round the next bend, not through the walls)
    this.target = this.lie.id === 'green' && !world.spec.green.channel ? { x: world.cup.x, z: world.cup.z, isPin: true } : aimTarget(world, ball.x, ball.z);
    this.targetDist = Math.hypot(this.target.x - ball.x, this.target.z - ball.z);
    this.aimAngle = Math.atan2(this.target.z - ball.z, this.target.x - ball.x);
    this.wantDist = null;
    this.puttPct = null;
    this.previewUntil = 0;
    this.setClub(autoSelectClub(this.targetDist, this.lie, this.onTee));
    this.toPin = toPin;

    this.round.live = {
      index: this.round.index, x: ball.x, z: ball.z,
      strokes: this.strokes, putts: this.putts, bonuses: this.bonuses, holePoints: this.holePoints,
      holeLog: this.holeLog,
    };
    store.set(SAVE_KEY, this.round);

    this.blocked = false;
    this.effects.aimArc.material.color.setHex(0xffffff);
    this.setState('aim');
    this.swing.phase = 'idle';
    this.charging = false;
    this.hud.setControlsVisible(true);
    this.hud.setSwinging(false);
    this.hud.setCarry(null);
    this.updateScoreHud();
    this.rig.cut();
    this.ballScale = 1.4;
    this.walkIn = { t: 0, dur: 1.0 };
    this.cheerSpot = null;
    this.net.atAim();
    this.hud.setMulligan(this.mulligans > 0 && !!this.lastFrom, () => this.takeMulligan());
    this.forage.atAim();
    this.showHint();
  }

  setClub(club) {
    this.club = club;
    this.planDirty = true;
    this.puttMax = null;
    if (!this.putting && this.wantDist != null) {
      const reach = distanceAt(club, 100, this.lie.speedFactor);
      this.wantDist = Math.max(reach * 0.15, Math.min(reach, this.wantDist));
    }
    if (this.putting) {
      // Putts always start aimed at the hole, whatever the path says (bar
      // crazy golf, where the path is the lane round the bends)
      if (!this.world.spec.green.channel) this.target = { x: this.world.cup.x, z: this.world.cup.z, isPin: true };
      this.targetDist = Math.hypot(this.target.x - this.ball.x, this.target.z - this.ball.z);
      this.effects.beads.layout(this.world, this.ball);
    } else {
      this.effects.beads.hide();
    }
    this.refreshClubHud();
  }

  availableClubs() {
    // Crazy golf is a putting challenge: the putter is the only club
    if (this.world?.spec.green.channel) return [PUTTER];
    return CLUBS.filter((c) => clubAllowed(c, this.lie, this.onTee));
  }

  cycleClub(dir) {
    if (this.state !== 'aim' || this.paused) return;
    const list = this.availableClubs();
    const i = list.indexOf(this.club);
    const next = list[Math.max(0, Math.min(list.length - 1, i + dir))];
    if (next && next !== this.club) {
      this.audio.tap();
      const wasPutting = this.putting;
      this.setClub(next);
      if (wasPutting !== this.putting) this.aimAngle = Math.atan2(this.world.cup.z - this.ball.z, this.world.cup.x - this.ball.x);
    }
  }

  /**
   * How far the shot goes, by hand: drag up for more, down for less (or
   * the mouse wheel). The club follows the distance, the meter's marker
   * follows the club, and the preview line shows exactly what you asked for.
   * On the green the same drag sets the pace of the putt.
   */
  adjustLength(dyPx) {
    if (this.state !== 'aim' || this.paused || !dyPx) return;
    const scale = 640 / Math.max(320, Math.min(window.innerHeight, 1000));
    if (this.putting) {
      const pct = this.puttPct ?? this.idealPct ?? 50;
      this.puttPct = Math.max(12, Math.min(96, pct - dyPx * 0.22 * scale));
    } else {
      const reach = distanceAt(this.club, 100, this.lie.speedFactor);
      const cur = this.wantDist ?? Math.min(reach, this.targetDist);
      const longest = this.availableClubs().filter((c) => c !== PUTTER).reduce((m, c) => Math.max(m, distanceAt(c, 100, this.lie.speedFactor)), 0);
      const want = Math.max(6, Math.min(longest || reach, cur - dyPx * 0.3 * scale));
      // Out of this club's range, or well inside it: move to the club that fits
      if (want > reach + 0.5 || want < reach * 0.5) {
        const pick = autoSelectClub(want, this.lie, this.onTee);
        if (pick && pick !== PUTTER && pick !== this.club && this.availableClubs().includes(pick)) { this.club = pick; this.puttMax = null; this.audio.tap(); }
      }
      const now = distanceAt(this.club, 100, this.lie.speedFactor);
      this.wantDist = Math.max(now * 0.15, Math.min(now, want));
    }
    this.previewUntil = performance.now() + 1100;
    this.planDirty = true;
    this.hud.demo(null);
    this.hud.hint('');
  }

  refreshClubHud() {
    const lie = this.lie;
    const penalty = Math.round((1 - lie.speedFactor) * 100);
    const lieText = this.onTee && !this.world.spec.green.channel ? 'TEE' : penalty ? `${lie.name.toUpperCase()} −${penalty}%` : lie.name.toUpperCase();
    if (this.putting) {
      this.hud.setClub({ name: 'Putter', yards: `${feet(this.targetDist)} ft to hole`, lie: lieText, canChange: this.availableClubs().length > 1 });
      this.hud.setLandTag('', 0, 0, false);
      this.hud.setAction('hold');
    } else {
      const max = Math.round(distanceAt(this.club, 100, lie.speedFactor));
      const want = Math.round(Math.min(max, this.wantDist ?? this.targetDist));
      this.hud.setClub({ name: this.club.name, yards: want < max - 1 ? `${want}y · max ${max}` : `${max}y max`, lie: lieText, lieBad: penalty > 0, canChange: true });
      this.hud.setAction('swing', this.fullPower);
    }
  }

  /** The shot needs everything the club has: power sets itself, two taps. */
  get fullPower() { return !this.putting && !this.idealPower; }

  /** Recompute the shot preview for the current aim/club. */
  updatePlan() {
    const { ball, world } = this;
    const dirX = Math.cos(this.aimAngle), dirZ = Math.sin(this.aimAngle);
    this.dirX = dirX; this.dirZ = dirZ;

    // A bounce mushroom near the line, and in reach: the shot locks on to it
    // (direction and distance), until the aim is swung well away again
    let lock = this.putting ? null : this.padLock();
    if (lock) { this.solveLock(lock); if (lock.power == null) { lock = null; this.padFar = true; this.dirX = dirX; this.dirZ = dirZ; } } // (out of reach after all)
    if (this.padFar && !lock && this.state === 'aim' && !this.farSaid) { this.farSaid = true; this.hud.callout('🍄 TOO FAR TO REACH FROM HERE', 'small'); }
    if (!this.padFar) this.farSaid = false;
    if (!!lock !== !!this.locked) {
      if (lock && this.state === 'aim') { this.hud.callout('🍄 LOCKED ON', 'small'); this.audio.tap(); }
      this.locked = lock;
    } else this.locked = lock;
    this.gimmicks.lockedPad = lock?.pad || null;
    if (this.putting) {
      // Pace for the cup, or (crazy golf) for the bend the putt is played to
      const to = this.target && !this.target.isPin ? this.target : world.cup;
      const dist = Math.hypot(to.x - ball.x, to.z - ball.z);
      this.readSpeed = puttSpeedFor(world, ball, dirX, dirZ, dist);
      if (!this.puttMax) {
        this.puttMax = puttMeterMax(this.readSpeed);
        // Crazy golf: a full putt should be a real smash, one that could roll
        // the whole rest of the lane on the flat and bank round the walls
        if (world.lane) {
          const pts = world.lane.pts, left = pathLength(pts) - pathInfo(pts, ball.x, ball.z).along;
          const flat = { ...flatWorld('green'), slick: world.slick };
          const smash = puttSpeedFor(flat, createBall(0, BALL_R, 0), 1, 0, Math.max(8, left * 1.05), 0);
          this.puttMax = Math.max(this.puttMax, smash);
        }
      }
      // The caddie's read is the pace to start from; a vertical drag sets your own
      this.readPct = Math.min(96, (this.readSpeed / this.puttMax) * 100);
      // Crazy golf: the caddie's pace is for the next bend, which is timid on
      // a lane made for banking. Along the line being aimed, try firmer paces
      // and start from the one that gets furthest round (or in)
      if (world.lane && this.puttPct == null) this.readPct = this.lanePace(dirX, dirZ, this.readPct);
      this.idealPct = this.puttPct ?? this.readPct;
      this.idealSpeed = puttSpeedAt(this.puttMax, this.idealPct);
      const preview = previewPutt(world, ball, dirX, dirZ, this.idealSpeed);
      this.effects.aimArc.hide();
      this.effects.ring.visible = false;
      const a = this.assists;
      // The whole roll, like the line on a pool table: where it goes, how it
      // breaks, where it stops (or drops)
      this.effects.showPutt(preview.points, a.confirmLine && preview.holed, preview);
      this.previewPts = preview.points;
      this.landing = null; this.rest = null;
      this.hud.showPuttMeter(this.idealPct);
      // A tick when the read is right (while that help lasts)
      this.hud.setMeterCaption(a.confirmLine && preview.holed ? 'ok' : '');
      this.hud.setMeterFlag(this.idealPct, 'release');
      this.hud.setMeterSteps(null);
    } else {
      const reach = distanceAt(this.club, 100, this.lie.speedFactor);
      const aimDist = lock ? lock.dist : this.wantDist ?? this.targetDist;
      const dirX = this.dirX, dirZ = this.dirZ; // (the locked line, if a mushroom has it)
      this.slope = slopeAlong(world, ball, dirX, dirZ);
      // The power at which a perfect swing STOPS the ball at the target (or at
      // the length dragged), worked out by test shots in still air: the wind
      // is the player's to allow for, nothing else. Re-solved when the club,
      // length or line changes; while the aim is being swung round, the last
      // answer stands and it is solved again once the aim settles.
      const book = reach >= aimDist ? powerFor(this.club, aimDist, this.lie.speedFactor) : null;
      const key = `${this.club.id}|${Math.round(aimDist * 2)}|${this.lie.id}|${ball.x.toFixed(1)},${ball.z.toFixed(1)}|${lock ? 'pad' : ''}`;
      const now = performance.now();
      const turned = this.cal && Math.abs(Math.atan2(Math.sin(this.aimAngle - this.cal.angle), Math.cos(this.aimAngle - this.cal.angle))) > 0.015;
      if (!this.cal || this.cal.key !== key || (turned && now - this.cal.at > 220)) {
        // (locked on a mushroom: solveLock has worked out the shot, wind and all)
        const solved = lock ? lock.power : powerForRest(world, ball, { club: this.club, lie: this.lie, dirX, dirZ, slope: this.slope, want: aimDist, book });
        this.cal = { key, angle: this.aimAngle, at: now, power: solved };
        this.calStale = false;
      } else if (turned) this.calStale = true; // (solved again once the aim settles: see update)
      const want = this.cal.power;
      // Near enough to everything the club has counts as a full shot: asking
      // for a tap a hair before the bar tops out is a trap, not a skill
      // (locked on a mushroom, the exact power is shown however near the top it is)
      this.idealPower = want && (want < 96 || lock) ? want : null;
      const launch = buildLaunch({
        club: this.club, power: this.idealPower ?? 100, dirX, dirZ, lie: this.lie, slope: this.slope,
      });
      const preview = previewShot(world, ball, launch);
      if (preview.blocked !== this.blocked) {
        this.blocked = preview.blocked;
        this.effects.aimArc.material.color.setHex(preview.blocked ? 0xff5a3c : 0xffffff);
        if (this.state === 'aim') {
          if (preview.blocked) this.hud.demo('drag', { warn: '🌲' });
          else this.showHint();
        }
      }
      this.effects.puttLine.hide();
      this.landing = { x: preview.landX, y: preview.landY, z: preview.landZ };
      const camDist = Math.hypot(preview.landX - this.camera.position.x, preview.landZ - this.camera.position.z);
      this.rest = { x: preview.restX, z: preview.restZ, water: preview.water };
      this.effects.showAim(preview.points, preview.landX, world.heightAt(preview.landX, preview.landZ), preview.landZ, camDist, preview.roll, this.rest);
      this.previewPts = preview.points.concat(preview.roll);
      if (this.state === 'aim' || this.state === 'swing') this.refreshClubHud();
      // Sky rings hang along the first tee shot the caddie lines up
      if (this.strokes === 0 && this.state === 'aim' && this.round) this.gimmicks.hangRings(preview.points);
      if (this.state === 'aim') { this.hud.hideMeter(); this.hud.setAction('swing', this.fullPower); }
    }
    this.planDirty = false;
  }

  showHint() {
    if (this.putting) {
      this.hud.demo(this.hints.putt < 3 ? 'drag' : null, { high: true });
      // (crazy golf: say it out loud, a few putts a round: this lane is for smashing)
      if (this.world.lane && (this.round?.laneHints || 0) < 3) this.hud.hint('💥 Hit it hard: bank it off the rails');
      else this.hud.hint(this.hints.putt < 3 ? (FINE_POINTER ? 'A D to aim · W S for pace' : 'Drag ← → to aim · ↑ ↓ for pace') : '');
    } else {
      this.hud.demo(this.hints.swing < 3 ? 'drag' : null);
      this.hud.hint(this.hints.swing < 3 ? (FINE_POINTER ? 'A D to aim · W S for distance' : 'Drag ← → to aim · ↑ ↓ for distance') : '');
    }
  }

  // ===========================================================================
  // Swing input
  // ===========================================================================

  /** Tap on the course (not a button). */
  tap(x, y) {
    if (this.paused) return;
    // A mushroom to gather, or a dog to pat?
    if (x !== undefined && ['aim', 'settle', 'result', 'summary', 'cook', 'nabe'].includes(this.state)) {
      if (this.forage.tapAt(x, y) || this.dog.tapAt(x, y)) return;
    }
    // Watching friends from the result card: a tap moves on to the next one
    if (this.state === 'result' && this.spectName) { this.net.spectNext(); this.audio.tap(); return; }
    if (this.state === 'intro' && this.stateTime > 0.5) this.endIntro();
    else if (this.state === 'swing' && !this.putting) this.advanceSwing();
    else if (this.state === 'swing' && this.putting && this.puttLatched) this.releasePutt();
    else if (this.state === 'flight') this.fastForward = true;
    else if (this.state === 'result') this.pressNext();
  }

  swingDown() {
    if (this.paused) return;
    if (this.state === 'fishing') { this.fishing.tap(); return; }
    if (this.state === 'intro') { if (this.stateTime > 0.5) this.endIntro(); return; }
    if (this.state === 'flight') { this.fastForward = true; return; }
    if (this.state === 'aim') {
      // With friends the first press says READY; the swing waits for GO
      if (!this.net.clearToSwing()) return;
      this.net.startSwing();
      this.walkIn = null; // swinging already: the camper is at the ball
      this.coachHeld = null;
      this.dwell = 0;
      if (this.planDirty) this.updatePlan();
      this.hud.hint('');
      if (this.putting) {
        this.charging = true;
        this.coach = [ 'hold', 0.6 ][this.hints.putt] ?? null;
        this.puttLatched = false;
        this.puttPower = 0;
        this.puttDir = 1;
        this.puttHold = 0;
        this.setState('swing');
        this.hud.setAction('release');
        this.hud.setMeterFlag(this.idealPct, 'release');
        this.hud.setMeterSteps(null);
        this.hud.demo(null);
      } else {
        startSwing(this.swing);
        this.lockGrace = 0;
        // First swings are coached: the meter waits for you, then runs slow
        this.coach = [ 'hold', 0.6, 0.8 ][this.hints.swing] ?? null;
        this.setState('swing');
        this.hud.setSwinging(true);
        this.hud.showSwingMeter(this.idealPower, this.coach ? 1 : 1 / (this.assists.tight * (this.mods?.tight ?? 1)));
        this.hud.setMeterSteps(2);
        if (this.idealPower) {
          this.hud.setMeterCaption('');
          this.hud.setMeterFlag(markerToPercent(this.idealPower), 'tap');
          this.hud.setAction('power');
        } else {
          // Full power: the bar fills itself to MAX, no second tap needed.
          // The one tap still to come is already marked, dimmed, on the line
          this.hud.setMeterCaption('');
          this.hud.setMeterAuto(true);
          this.hud.setMeterFlag(markerToPercent(SWING.LINE), 'tap', false, true);
          this.hud.setAction('wait');
        }
        this.hud.demo(null);
        this.audio.tap();
      }
    } else if (this.state === 'swing') {
      if (this.putting) { if (this.puttLatched) this.releasePutt(); } else this.advanceSwing();
    }
  }

  swingUp() {
    if (this.state !== 'swing' || !this.putting || !this.charging || this.puttLatched) return;
    if (this.stateTime < 0.22 || this.puttPower < 12) {
      // A quick tap rather than a hold: keep the bar running and let the
      // next tap play the stroke, so both habits work.
      this.puttLatched = true;
      this.hud.setAction('tap');
      this.hud.setMeterFlag(this.idealPct, 'tap');
      return;
    }
    this.releasePutt();
  }

  releasePutt() {
    this.charging = false;
    this.puttLatched = false;
    this.hitPutt(Math.max(4, this.puttPower));
  }

  advanceSwing() {
    // Swallow the over-eager tap right after power locks (a double tap, or a
    // tap that arrives as the bar tops out) so it cannot ruin the strike.
    if (this.lockGrace > 0) return;
    const event = swingClick(this.swing);
    if (event) this.handleSwingEvent(event);
  }

  handleSwingEvent(event) {
    if (event.type === 'powerLocked') {
      this.lockGrace = this.steady ? 0.35 : 0.2;
      this.dwell = 0; // steady mode: a fresh pause at the line
      this.audio.powerLock(event.power);
      this.hud.setAction('strike', !this.idealPower);
      this.hud.setMeterCaption('');
      this.hud.setMeterFlag(markerToPercent(SWING.LINE), 'tap');
      this.hud.setMeterSteps(3);
    } else if (event.type === 'strike') {
      this.hitShot(event.timing);
    }
  }

  /**
   * Swiping during a well-struck shot works the ball in the air: sideways
   * bends it, up/down takes spin off or puts it on. Something to do with your
   * thumb while the ball is flying.
   */
  afterTouch(dx, dy) {
    const { ball, shot } = this;
    if (!shot || shot.putt || !shot.shapeable || ball.mode !== 'air' || ball.landed) return;
    const k = (420 / Math.max(320, Math.min(window.innerWidth, 900))) * (this.steady ? 0.6 : 1);
    const side = Math.max(-1.3, Math.min(1.3, shot.bend + dx * 0.0045 * k));
    ball.side += side - shot.bend;
    shot.bend = side;
    const back = Math.max(-0.6, Math.min(0.9, shot.bite + dy * 0.004 * k));
    ball.back = Math.max(0, ball.back + (back - shot.bite));
    shot.bite = back;
    shot.replan = true;
    if (!shot.shaped) {
      shot.shaped = true;
      this.hud.demo(null);
      if (this.hints.spin < 4) { this.hints.spin += 1; store.set(HINT_KEY, this.hints); }
    }
  }

  // ===========================================================================
  // Hitting
  // ===========================================================================

  beginFlight(wasPutt) {
    const { ball } = this;
    // (where it was, for a Mulligan: the stroke count before this shot, penalties and all)
    this.lastFrom = { x: ball.x, z: ball.z, strokes: this.strokes, putts: this.putts };
    this.hud.setMulligan(false);
    this.shot = {
      putt: wasPutt,
      fromX: ball.x, fromZ: ball.z,
      fromSurface: this.onTee ? 'tee' : ball.surface,
      toPin: this.toPin,
      treeCalled: false,
      events: [], // (for the room's feed: what the shot did)
      shapeable: false, bend: 0, bite: 0, replan: false, shaped: false,
    };
    this.follow = { t: 0, putt: wasPutt, dirX: this.dirX, dirZ: this.dirZ, x: ball.x, y: ball.y, z: ball.z, kind: this.clubKind(), size: this.clubSize || 1.3 };
    this.hud.setMeterFlag(null);
    this.hud.setMeterSteps(null);
    this.hud.setSwinging(false);
    this.strokes += 1;
    this.round.stats.swings += 1;
    this.updateScoreHud(`SHOT ${this.strokes}`);
    this.hud.hideMeter();
    this.hud.demo(null);
    this.hud.setControlsVisible(false);
    this.effects.hideAim();
    this.effects.beads.hide();
    this.setState('flight');
    this.simAccumulator = 0;
  }

  /** Dry-run the shot so the camera knows where the ball will end up. */
  planShot() {
    const b = copyBall(this.ball);
    const events = [];
    let t = 0, landTime = b.landed ? b.time : null;
    while ((b.mode === 'air' || b.mode === 'roll') && t < 40) {
      stepBall(b, this.world, this.env, SIM_DT, events);
      t += SIM_DT;
      if (landTime === null && b.landed) landTime = b.time;
    }
    return {
      landTime: landTime ?? b.time,
      time: t,
      restX: b.x, restZ: b.z,
      landX: b.landed ? b.landX : b.x, landZ: b.landed ? b.landZ : b.z,
      holed: b.mode === 'holed',
      lipout: events.some((e) => e.type === 'lipout'),
    };
  }

  hitShot(timing) {
    const { ball } = this;
    // Later in the round the same miss costs more
    const tight = (this.coach ? 1 : this.assists.tight * (this.mods?.tight ?? 1)) * (this.steady ? 0.55 : 1);
    let strike = strikeFromTiming(Math.max(-1, Math.min(1, timing * tight)), this.club.loft, this.rng);
    // Locked on a mushroom, the meter is forgiving: a tap near the flag counts
    // as on it, and only a truly mistimed strike pushes the ball off the line
    let power = this.swing.power;
    if (this.locked && this.idealPower && Math.abs(power - this.idealPower) <= 10) power = this.idealPower;
    if (this.locked && strike.grade !== 'terrible') strike = { ...strike, yawDeg: 0, sidespinAdd: 0, speedFactor: 1, loftDelta: 0 };
    const launch = buildLaunch({
      club: this.club, power, dirX: this.dirX, dirZ: this.dirZ,
      lie: this.lie, strike, scatter: this.locked ? 0 : this.rng() * 2 - 1, slope: this.slope,
    });
    this.beginFlight(false);
    launchBall(ball, launch);
    this.net.shot({ kind: 'full', x: this.shot.fromX, z: this.shot.fromZ, launch });
    this.plan = this.planShot();
    this.shot.chase = this.plan.landTime > 2.1;
    // A decent strike can be worked in the air
    this.shot.strike = strike;
    this.shot.lockPad = this.locked?.pad || null; // (a locked shot homes in on its mushroom: see updateFlight)
    this.shot.power = this.swing.power;
    this.shot.shapeable = strike.grade === 'pure' || strike.grade === 'good';
    if (this.shot.shapeable && this.shot.chase && this.hints.spin < 4) {
      this.hud.demo('shape', { low: true }); // (below the ball, not over it)
    }

    const pure = strike.grade === 'pure';
    if (pure) {
      this.dog.on('pure'); this.camper.feel('joy', 1.6);
      // Hit-stop: the ball holds on the face for a blink, and the frame jolts
      this.hitStop = 0.08;
      this.rig.addShake(0.05);
      this.audio.vibrate(18);
    }
    else if (strike.grade === 'terrible') this.camper.feel('oops', 1.6);
    this.audio.strike(this.swing.power, strike.grade);
    this.effects.strikeFlash(ball.x, ball.y, ball.z, pure);
    this.effects.startTrail(pure ? 0xffd84a : 0xffffff);
    this.rig.addShake(pure ? 0.22 : 0.12);
    this.hud.callout(describeStrike(strike), pure ? 'gold' : strike.grade === 'good' ? '' : 'bad');

    const round = this.round;
    if (pure) {
      round.pureStreak += 1;
      this.holeLog.pures += 1;
      round.stats.pures += 1;
      this.award(round.pureStreak > 1 ? `Pure strike ×${round.pureStreak}` : 'Pure strike', 50 * Math.min(5, round.pureStreak), true);
    } else {
      round.pureStreak = 0;
    }
    if (this.hints.swing < 3) { this.hints.swing += 1; store.set(HINT_KEY, this.hints); }
  }

  hitPutt(power) {
    if (this.world.lane && this.round) this.round.laneHints = (this.round.laneHints || 0) + 1;
    const speed = puttSpeedAt(this.puttMax, power);
    this.beginFlight(true);
    this.putts += 1;
    this.round.stats.putts += 1;
    puttBall(this.ball, { speed, dirX: this.dirX, dirZ: this.dirZ });
    this.net.shot({ kind: 'putt', x: this.shot.fromX, z: this.shot.fromZ, speed, dirX: this.dirX, dirZ: this.dirZ });
    this.plan = this.planShot();
    this.audio.putt(speed);
    const off = (power - this.idealPct) / this.idealPct;
    if (Math.abs(off) < 0.06) this.hud.callout('PERFECT PACE', 'gold small');
    if (this.hints.putt < 3) { this.hints.putt += 1; store.set(HINT_KEY, this.hints); }
  }

  /**
   * The mischief made visible: rain falling on my screen and a cloud over my
   * head (Rain Cloud), my camper swaying and my swing meter shaking (The
   * Wobbles). Cleared at the next hole.
   */
  gags(mods) {
    if (this.rainFx) { this.scene.remove(this.rainFx); this.rainFx.geometry.dispose(); this.rainFx = null; }
    if (this.myCloud) { this.scene.remove(this.myCloud); this.myCloud = null; }
    this.sky.gloom = mods.rain ? 1 : 0;
    if (mods.rain) {
      this.rainFx = buildRain(this.world, this.coarse);
      if (this.rainFx) this.scene.add(this.rainFx);
      this.myCloud = makeRainCloud();
      this.scene.add(this.myCloud);
    }
    this.camper.wobbly = !!mods.wobble;
    this.hud.root.classList.toggle('wobbly', !!mods.wobble);
    this.sky.fogBank = mods.fog ? 1 : 0;
  }

  /**
   * The bounce mushroom the shot should lock on to, if any: ahead, within a
   * few metres of the line the player is aiming, and within the club's reach
   * (with a little spare). { angle, dist, pad } or null.
   */
  padLock() {
    const pads = this.gimmicks?.pads;
    if (!pads?.length) return null;
    if (this.state === 'swing') return this.locked || null; // (held through the swing)
    const { ball } = this;
    const reach = distanceAt(this.club, 100, this.lie.speedFactor) * 1.15;
    let best = null;
    this.padFar = false;
    for (const p of pads) {
      const dx = p.x - ball.x, dz = p.z - ball.z, d = Math.hypot(dx, dz);
      const a = Math.atan2(dz, dx);
      const off = Math.abs(Math.atan2(Math.sin(a - this.aimAngle), Math.cos(a - this.aimAngle)));
      // (aimed at, but the club cannot get there: say so, rather than nothing happening)
      if (d > reach && off < 0.6 && Math.sin(Math.min(off, 1.5)) * d < 7) this.padFar = true;
      if (d < 12 || d > reach) continue;
      // within about 4.5 m of the aim line at the mushroom (more while already locked: it is sticky)
      if (Math.sin(Math.min(off, 1.5)) * d > (this.locked?.pad === p ? 10 : 7) || off > 0.6) continue;
      if (!best || off < best.off) best = { angle: a, dist: d, pad: p, off };
    }
    return best;
  }

  /**
   * A locked shot really lands on the mushroom: the power that brings it down
   * through the cap's height at the mushroom, and the line turned into the
   * wind so the drift carries it on. Kept until the club, lie or ball moves.
   */
  solveLock(lock) {
    const { ball, world } = this;
    const key = `${lock.pad.x},${lock.pad.z}|${this.club.id}|${this.lie.id}|${ball.x.toFixed(1)},${ball.z.toFixed(1)}`;
    if (this.lockSolve?.key !== key) {
      let angle = lock.angle, power = null;
      const atY = lock.pad.top + 0.2;
      for (let i = 0; i < 3; i++) {
        const dx = Math.cos(angle), dz = Math.sin(angle);
        const slope = slopeAlong(world, ball, dx, dz);
        const o = { club: this.club, lie: this.lie, dirX: dx, dirZ: dz, slope, env: this.env, atY };
        power = powerForCarry(world, ball, { ...o, want: lock.dist, book: powerFor(this.club, lock.dist, this.lie.speedFactor) });
        if (power === null) break;
        const at = carryPoint(world, ball, { ...o, power });
        // sideways miss (+ left of the line to the mushroom), turned back the other way
        const ux = Math.cos(lock.angle), uz = Math.sin(lock.angle);
        const side = (at.x - lock.pad.x) * -uz + (at.z - lock.pad.z) * ux;
        angle -= Math.atan2(side, lock.dist);
      }
      this.lockSolve = { key, angle, power };
    }
    lock.power = this.lockSolve.power;
    this.dirX = Math.cos(this.lockSolve.angle); this.dirZ = Math.sin(this.lockSolve.angle);
  }

  /** The meter reading along (dirX, dirZ) that gets the ball furthest round the lane (or in). */
  lanePace(dirX, dirZ, read) {
    const { ball, world } = this;
    const key = `${ball.x.toFixed(1)},${ball.z.toFixed(1)}|${Math.round(this.aimAngle * 40)}`;
    if (this.paceCache?.key === key) return this.paceCache.pct;
    const pts = world.lane.pts, total = pathLength(pts);
    let best = null;
    for (const k of [1, 1.35, 1.75, 2.3, 3]) {
      const pct = Math.min(96, read * k);
      const pv = previewPutt(world, ball, dirX, dirZ, puttSpeedAt(this.puttMax, pct));
      const left = pv.holed ? -1 : total - pathInfo(pts, pv.endX, pv.endZ).along + Math.hypot(pv.endX - world.cup.x, pv.endZ - world.cup.z) * 0.3;
      if (!best || left < best.left - 0.5) best = { pct, left };
      if (pct >= 96) break;
    }
    this.paceCache = { key, pct: best.pct };
    return best.pct;
  }

  /** This hole's cards and perk, applied to a lie: rough and sand may cost half. */
  perkLie(lie) {
    const p = this.mods;
    if (!p) return lie;
    let sf = lie.speedFactor;
    if (p.lieHalf && sf < 1) sf = 1 - (1 - sf) * 0.5;
    if (p.power && p.power !== 1) sf *= p.power; // (the Rocket Ball)
    return sf === lie.speedFactor ? lie : { ...lie, speedFactor: sf };
  }

  /** Add points; `now` pops it on screen immediately. */
  award(label, points, now = false) {
    this.bonuses.push({ label, points });
    this.holePoints += points;
    this.round.points += points;
    this.hud.setPoints(this.round.points, true);
    if (now) {
      this.hud.callout(`+${points}`, 'pts');
      this.audio.reward(this.bonuses.length);
    }
  }

  // ===========================================================================
  // Flight
  // ===========================================================================

  updateFlight(dt) {
    const { ball, world, plan, shot } = this;

    // Drama: slow the last moment of a putt that is going to scare the hole
    let scale = 1;
    const cupDist = Math.hypot(world.cup.x - ball.x, world.cup.z - ball.z);
    if (shot.putt && (plan.holed || plan.lipout) && cupDist < 1.6 && cupDist > 0.2) scale = 0.4;
    else if (this.fastForward && ball.mode === 'roll') scale = 3;
    // A long roll speeds itself up: nobody should stand watching a ball crawl
    else if (ball.mode === 'roll' && ball.time > 6) scale = 2.5;
    this.timeScale += (scale - this.timeScale) * Math.min(1, dt * 10);
    // ...and a long flight says it can be hurried
    if (this.stateTime > 4 && !shot.ffHinted && !this.fastForward) { shot.ffHinted = true; this.hud.hint('Tap to speed it up ⏩'); }

    // (a pure strike holds the ball on the face for a moment first)
    if (this.hitStop > 0) { this.hitStop -= dt; return; }
    this.simAccumulator += dt * this.timeScale;
    this.events.length = 0;
    let steps = 0;
    while (this.simAccumulator >= SIM_DT && steps < 40 && (ball.mode === 'air' || ball.mode === 'roll')) {
      stepBall(ball, world, this.env, SIM_DT, this.events);
      // Off a friend's head, or into a friend's ball: a new flight, so a new plan
      if (this.net.bonkCheck(ball, null, null, this.shot.putt)) { this.plan = this.planShot(); this.shot.events.push('bonk'); }
      if (this.net.knockCheck(ball)) { this.plan = this.planShot(); this.shot.events.push('knock'); }
      // A shot locked on a mushroom homes in as it comes down near it: a near
      // miss (a tap a hair early or late on the meter) still finds the cap
      const lp = this.shot.lockPad;
      if (lp && ball.mode === 'air' && ball.vy < 0 && ball.y > lp.top) {
        const hx = lp.x - ball.x, hz = lp.z - ball.z, hd = Math.hypot(hx, hz);
        if (hd < 12 && hd > 0.2) {
          // sideways only (across the ball's path): pulled along it, a short
          // ball would speed up and sail over the cap
          const vh = Math.hypot(ball.vx, ball.vz) || 1, px = -ball.vz / vh, pz = ball.vx / vh;
          const side = hx * px + hz * pz;
          const pull = Math.sign(side) * Math.min(Math.abs(side), 2.5) * 12 * SIM_DT;
          ball.vx += px * pull;
          ball.vz += pz * pull;
        }
      }
      // A ball coming down onto a mushroom's cap springs off it there (not on the grass beneath)
      if (ball.mode === 'air' && ball.vy < 0 && this.gimmicks.pads.length && !this.shot.putt) {
        const pad = this.gimmicks.padAt(ball.x, ball.z);
        // (a shot locked on this one is caught a little above the cap: the lock-on is generous)
        const catchAt = pad === this.shot.lockPad ? 1.6 : 0.15;
        if (pad && ball.y <= pad.top + catchAt && ball.y > pad.top - 1.2) { this.boing({ type: 'land' }); this.plan = this.planShot(); }
      }
      this.simAccumulator -= SIM_DT;
      steps++;
      if (this.gimmicks.rings.length) this.checkRings(); // every step: a fast ball must not skip a hoop
    }

    // A ball that will not stop rolling (a steep bowl, a seam in the grid)
    // must never hold the hole hostage: after a while it is simply down
    if (ball.mode === 'roll' && ball.time > 22) {
      ball.vx = ball.vz = ball.vy = 0;
      ball.mode = 'rest';
      this.events.push({ type: 'rest', x: ball.x, y: ball.y, z: ball.z, surface: ball.surface });
    }
    if (shot.replan && ball.mode === 'air') {
      shot.replan = false;
      this.plan = this.planShot();
    }
    if (!shot.putt) {
      // Live yardage: the number climbing is half the fun of a good drive
      let sub = '';
      if (shot.shaped) {
        const b = Math.round(Math.abs(shot.bend) / 1.3 * 100);
        const parts = [];
        if (b >= 5) parts.push(shot.bend < 0 ? `↶ DRAW ${b}%` : `FADE ${b}% ↷`);
        if (Math.abs(shot.bite) > 0.08) parts.push(shot.bite > 0 ? 'BACKSPIN' : 'RUN');
        sub = parts.join(' · ');
      } else if (shot.shapeable && ball.mode === 'air' && !ball.landed) {
        sub = 'SWIPE TO SHAPE';
      }
      this.hud.setCarry(`${Math.round(Math.hypot(ball.x - shot.fromX, ball.z - shot.fromZ))}y`, sub);
    }

    for (const e of this.events) this.handleBallEvent(e);
    if (this.state !== 'flight') return;

    // Camera
    if (shot.putt) {
      // Walk in behind the ball as it tracks toward the hole
      const d = cupDist || 1;
      // (no controls on screen now, so frame the ball lower)
      this.rig.putt(ball, (world.cup.x - ball.x) / d, (world.cup.z - ball.z) / d, Math.max(1.2, cupDist), 0.9 + cupDist * 0.07);
      this.rig.stiffness = 2.2;
    } else if (shot.chase && ball.time < 0.32 && ball.mode === 'air') {
      // A beat on the strike: hold where we are and watch it leave the face,
      // rather than sweep through the golfer's head into the chase
      this.rig.watch(ball, 0.05);
      this.rig.stiffness = 3;
    } else if (shot.chase && Math.hypot(plan.landX - shot.fromX, plan.landZ - shot.fromZ) < 85 && ball.mode === 'air') {
      this.rig.approach(ball, plan);
    } else if (shot.chase) {
      if (ball.time > plan.landTime - 1.05 || ball.mode !== 'air') {
        this.rig.landing(ball, plan, shot.fromX, shot.fromZ);
      } else {
        this.rig.chase(ball);
      }
    } else {
      this.rig.watch(ball, 0.1);
    }
  }

  /** Mushrooms: anything that touches down on one springs away. */
  boing(e) {
    const { ball, shot } = this;
    if (shot.putt || (e.type !== 'land' && e.type !== 'bounce' && e.type !== 'roll')) return;
    const pad = this.gimmicks.padAt(ball.x, ball.z);
    if (!pad) return;
    this.gimmicks.bouncePad(pad);
    const speed = Math.hypot(ball.vx, ball.vz);
    const k = speed > 0.5 ? Math.max(1, 9 / speed) : 0; // always leaves with some pace
    ball.vx *= k; ball.vz *= k;
    if (!k) { ball.vx = this.dirX * 9; ball.vz = this.dirZ * 9; }
    ball.vy = 13;
    ball.y = Math.max(ball.y, pad.top + 0.1);
    ball.mode = 'air';
    shot.replan = true;
    this.hud.callout('BOING!', 'gold');
    this.dog.on('boing');
    this.camper.feel('joy', 1.5);
    this.award('Mushroom bounce', 100, true);
    this.audio.bounce(18, 'green');
    this.effects.puff(ball.x, ball.y, ball.z, 'green', 1.4);
  }

  /** Sky rings: points for each, more for a run, and a kick of speed for all three. */
  checkRings() {
    const { ball, shot } = this;
    if (shot.putt || ball.mode !== 'air') return;
    for (const ring of this.gimmicks.ringsHit(ball)) {
      shot.rings = (shot.rings || 0) + 1;
      this.hud.callout(shot.rings > 1 ? `RING ×${shot.rings}` : 'RING!', 'gold small');
      this.dog.on('ring');
      this.camper.feel('joy', 1.2);
      this.award(`Sky ring ×${shot.rings}`, 150 * shot.rings, true);
      this.effects.strikeFlash(ring.p.x, ring.p.y, ring.p.z, true);
      if (this.gimmicks.ringsDone) {
        ball.vx *= 1.06; ball.vz *= 1.06;
        shot.replan = true;
        this.hud.callout('RING MASTER!', 'gold');
        this.award('All three rings', 500, true);
        this.audio.fanfare(2);
      }
    }
  }

  handleBallEvent(e) {
    const fx = this.effects;
    this.boing(e);
    this.shot?.events?.push(e.type);
    if (e.type === 'land' && this.shot.fromSurface === 'tee' && !this.shot.bull) {
      this.shot.bull = true;
      const hit = this.gimmicks.bullseye(e.x, e.z);
      if (hit) {
        this.hud.callout(hit === 3 ? 'BULLSEYE!' : hit === 2 ? 'ON TARGET' : 'TARGET', hit === 3 ? 'gold' : 'gold small');
        this.award(hit === 3 ? 'Bullseye' : 'On the target', [0, 100, 250, 500][hit], true);
        if (hit === 3) this.audio.fanfare(2);
      }
    }
    switch (e.type) {
      case 'land':
        if (this.hints.spin < 4) this.hud.demo(null);
        fx.puff(e.x, e.y, e.z, e.surface, Math.min(1.6, e.speed / 18));
        this.audio.bounce(e.speed * 0.5, e.surface);
        break;
      case 'bounce':
        fx.puff(e.x, e.y, e.z, e.surface, Math.min(1, e.speed / 12));
        this.audio.bounce(e.speed, e.surface);
        break;
      case 'tree':
        this.audio.tree();
        fx.puff(e.x, e.y, e.z, 'rough', 1.2);
        if (!this.shot.treeCalled) { this.hud.callout('TIMBER!', 'bad small'); this.shot.treeCalled = true; }
        break;
      case 'roof':
        // A cave's roof: a clonk, a puff of grit, and the bats are not pleased
        this.audio.tree();
        fx.puff(e.x, e.y, e.z, 'bunker', 1.2);
        if (!this.shot.roofCalled) { this.hud.callout('CLONK! MIND THE ROOF', 'bad small'); this.shot.roofCalled = true; }
        break;
      case 'wall':
        // Off the rail: a crazy-golf clack
        this.audio.bounce(Math.min(8, e.speed * 2), 'tee');
        break;
      case 'lipout':
        this.audio.lipOut();
        this.hud.callout('LIP OUT!', 'bad');
        break;
      case 'splash':
        this.reportShot('water');
        fx.splash(e.x, e.y, e.z);
        this.audio.splash();
        this.dog.on('splash');
        this.camper.feel('oops', 2.5);
        this.penalty(this.world.biome.liquid === 'lava' ? 'LAVA' : 'WATER');
        break;
      case 'oob':
        this.reportShot('oob');
        this.penalty('OUT OF BOUNDS');
        break;
      case 'holed':
        this.reportShot('holed');
        this.holed(e);
        break;
      case 'rest':
        this.reportShot('rest');
        this.atRest();
        break;
      default:
        break;
    }
  }

  /**
   * A friend's ball ran into mine while I was lining up: it rolls away (or
   * in!) at speed v, and no stroke is counted. Returns false if I was busy
   * (mid-swing): the knock is ignored here and my next shot puts it right.
   */
  knocked(vx, vz, by) {
    if (this.state !== 'aim' || this.paused || this.walkIn) return false;
    const { ball } = this;
    const speed = Math.hypot(vx, vz);
    if (!(speed > 0.05)) return false;
    this.shot = {
      putt: true, knocked: true, gimme: true, reported: true, by,
      fromX: ball.x, fromZ: ball.z, fromSurface: ball.surface, toPin: this.toPin, treeCalled: false, events: [],
      shapeable: false, bend: 0, bite: 0, replan: false, shaped: false,
    };
    this.follow = { t: 0, putt: true, dirX: vx / speed, dirZ: vz / speed, x: ball.x, y: ball.y, z: ball.z, kind: 'putter', size: this.clubSize || 1.3 };
    this.hud.setMeterFlag(null);
    this.hud.hideMeter();
    this.hud.demo(null);
    this.hud.setControlsVisible(false);
    this.effects.hideAim();
    this.effects.beads.hide();
    puttBall(ball, { speed, dirX: vx / speed, dirZ: vz / speed });
    this.plan = this.planShot();
    this.setState('flight');
    this.simAccumulator = 0;
    this.hud.callout(`🎱 KNOCKED BY ${by.toUpperCase()}!`, 'bad');
    this.camper.feel('oops', 2);
    this.audio.bounce(6, 'green');
    return true;
  }

  /** Tabi's shop, over the result card. */
  openShop() {
    const { items, targets } = this.net.shopStock();
    this.cardOpen = true; // (keep the result card from tucking away under it)
    this.hud.showShop({
      points: this.round.points, items, targets,
      onBuy: (id, to) => this.net.buy(id, to),
      onClose: () => { const btn = this.hud.layer.querySelector('.shop-btn b'); if (btn) btn.textContent = `★ ${this.round.points.toLocaleString()}`; },
    });
    this.audio.tap();
  }

  /** A Mulligan (from Tabi's shop): the last shot never happened. */
  takeMulligan() {
    const f = this.lastFrom;
    if (this.state !== 'aim' || !f || !(this.mulligans > 0)) return;
    this.mulligans -= 1;
    this.lastFrom = null;
    this.strokes = f.strokes;
    this.putts = f.putts;
    placeBall(this.ball, this.world, f.x, f.z);
    this.hud.callout('↩️ MULLIGAN!', 'small');
    this.audio.whoosh();
    this.updateScoreHud();
    this.beginAim();
  }

  /** Tell the room what my shot did, if it was anything worth hearing (once a shot). */
  reportShot(mode) {
    const { shot, ball, world } = this;
    if (!shot || shot.reported || !this.net.active) return;
    shot.reported = true;
    const h = classify({
      putt: shot.putt, fromSurface: shot.fromSurface, fromX: shot.fromX, fromZ: shot.fromZ,
      cupX: world.cup.x, cupZ: world.cup.z, mode, surface: ball.surface, x: ball.x, z: ball.z,
      events: shot.events, strokes: this.strokes, par: world.spec.par, lane: !!world.spec.green.channel,
    });
    if (h) this.net.happen(h);
  }

  penalty(label) {
    this.effects.endTrail();
    this.camper.feel('oops', 2.2);
    this.round.pureStreak = 0;
    this.holeLog.dirty = true;
    this.hud.setCarry(null);
    this.hud.callout(label, 'bad');
    this.ballMesh.visible = this.blob.visible = false;
    this.setState('settle');
    if (label === 'WATER') {
      // Water gives one chance to fish the ball out before the stroke is added
      this.settleFor = 0.9;
      this.afterSettle = () => this.goFishing();
    } else {
      this.takePenalty();
    }
  }

  /** The fishing minigame: hook the sinking ball and the penalty is forgiven. */
  goFishing() {
    const a = this.audio;
    this.setState('fishing');
    this.hud.setControlsVisible(false); // (the club and swing button come back with the ball)
    // Two games take turns. Lose two balls running and the third trip is the
    // golden ball: catch it and those two strokes come back as well, so the
    // water has cost nothing. It can never give back more than it took.
    const round = this.round;
    const trips = round.stats.waters = (round.stats.waters || 0) + 1;
    const mode = (round.waterLost || 0) >= 2 ? 'gold' : ['drop', 'steer'][(trips - 1) % 2];
    this.fishing.start({
      mode,
      level: this.assists.level,
      color: this.world.biome.water,
      hint: this.hints.fish < 6,
      sounds: {
        drop: () => a.whoosh(),
        hooked: () => a.tap(), bump: () => a.bounce(6, 'rough'),
        catch: () => { a.reward(4); a.vibrate?.(30); },
        fish: () => a.reward(0),
        miss: () => a.penalty(),
      },
      onDone: (result) => {
        if (this.hints.fish < 6) { this.hints.fish += 1; store.set(HINT_KEY, this.hints); }
        this.setState('settle');
        // Only balls lost in a row count toward the golden one
        round.waterLost = result === 'ball' || mode === 'gold' ? 0 : (round.waterLost || 0) + 1;
        // Every fish in the basket is dinner later
        const caught = this.fishing.caught;
        if (caught) {
          this.round.fish = (this.round.fish || 0) + caught;
          this.round.fishTotal = (this.round.fishTotal || 0) + caught;
          this.dog.on('fish');
          this.camper.feel('joy', 2);
          this.earn('fish', caught);
          this.hud.callout(caught > 1 ? `${caught} FISH!` : 'A FISH!', 'small');
          this.award(caught > 1 ? `Caught ${caught} fish` : 'Caught a fish', 50 * caught, true);
          this.net.happen({ k: 'fish', v: caught });
        }
        if (result === 'gold') {
          // The golden ball: no stroke this time, and the last two are refunded
          this.round.gold = (this.round.gold || 0) + 1;
          this.earn('gold');
          this.round.stats.fished = (this.round.stats.fished || 0) + 1;
          this.hud.callout('GOLDEN BALL!', 'gold');
          this.hud.callout('3 STROKES SAVED', 'gold small');
          this.award('Golden ball', 1000, true);
          this.audio.fanfare?.(2);
          this.updateScoreHud();
          this.dropBall(1.6);
        } else if (result === 'ball') {
          this.round.stats.fished = (this.round.stats.fished || 0) + 1;
          this.hud.callout('SAVED!', 'gold');
          this.hud.callout('NO PENALTY', 'gold small');
          this.award('Fished it out', 150, true);
          this.dropBall(1.1);
        } else {
          this.takePenalty();
        }
      },
    });
  }

  takePenalty() {
    this.strokes += 1;
    this.hud.callout('+1 PENALTY', 'bad small');
    this.audio.penalty();
    this.updateScoreHud();
    this.dropBall(1.5);
  }

  /** Back on dry land after a hazard, once the callouts have had their moment. */
  dropBall(wait) {
    this.setState('settle');
    this.settleFor = wait;
    this.afterSettle = () => {
      const { ball, world, shot } = this;
      // Drop a touch back from where it last crossed dry land
      let x = ball.dryX, z = ball.dryZ;
      const dx = shot.fromX - x, dz = shot.fromZ - z;
      const d = Math.hypot(dx, dz);
      if (d > 3) { x += (dx / d) * 2; z += (dz / d) * 2; }
      if (world.surfaceAt(x, z) === 'water') { x = shot.fromX; z = shot.fromZ; }
      placeBall(ball, world, x, z);
      this.ballMesh.visible = this.blob.visible = true;
      if (!this.pickUpIfDone()) this.beginAim();
    };
  }

  /** Too many strokes: concede the hole so a bad one never stalls the round. */
  pickUpIfDone() {
    const limit = this.world.spec.par + 5;
    if (this.strokes < limit) return false;
    this.strokes = limit;
    this.hud.callout('PICKED UP', 'bad');
    this.finishHole();
    return true;
  }

  atRest() {
    const { ball, world, shot, round } = this;
    this.effects.endTrail();
    const toPin = Math.hypot(world.cup.x - ball.x, world.cup.z - ball.z);
    // Knocked by a friend's ball: no stroke, no fuss, line it up again
    if (shot.knocked) { this.hud.callout(`${feet(toPin)} ft left`, 'small'); this.beginAim(); return; }
    const carried = Math.hypot(ball.x - shot.fromX, ball.z - shot.fromZ);
    if (this.strokes === 1) this.holeLog.teeToPin = toPin; // the par-3 contest
    const surface = ball.surface;
    const par = world.spec.par;

    if (shot.putt) {
      if (toPin < this.assists.gimme) {
        // Conceded: knock it in without making the player line up a six-incher
        this.strokes += 1;
        this.putts += 1;
        round.stats.putts += 1;
        this.shot.gimme = true;
        this.hud.callout('TAP-IN', 'small');
        const d = toPin || 1;
        puttBall(ball, { speed: 1.1, dirX: (world.cup.x - ball.x) / d, dirZ: (world.cup.z - ball.z) / d });
        this.plan = this.planShot();
        this.updateScoreHud();
        return;
      }
      this.hud.callout(`${feet(toPin)} ft left`, 'small');
      if (shot.toPin > 8 && toPin < 1.2) this.award('Great lag putt', 100, true);
    } else {
      if (shot.fromSurface === 'tee' && par > 3) {
        round.stats.fairwayChances += 1;
        round.stats.longestDrive = Math.max(round.stats.longestDrive, Math.round(carried));
        this.holeLog.longestDrive = Math.round(carried);
        if (surface === 'fairway') {
          this.holeLog.fairway = true;
          this.earn('fairways');
          round.stats.fairways += 1;
          this.hud.callout('FAIRWAY!', '');
          this.award('Fairway hit', 100, true);
          if (carried >= 195) { this.hud.callout(`${Math.round(carried)}y BOMB`, 'gold small'); this.award('Big drive', 100); }
        }
      }
      if (surface === 'rough' || surface === 'bunker') this.holeLog.dirty = true;
      if (surface === 'green') {
        if (this.holeLog.onGreenIn === null) {
          this.holeLog.onGreenIn = this.strokes;
          this.holeLog.firstProximity = toPin;
        }
        if (shot.fromSurface !== 'green') {
          this.hud.callout('ON THE GREEN', '');
          this.award('Hit the green', 150, true);
          if (this.strokes <= par - 2) { round.stats.gir += 1; this.award('Green in regulation', 100); }
        }
        if (toPin * 3 <= 6) { this.hud.callout(`STONE DEAD · ${feet(toPin)} ft`, 'gold small'); this.award('Stone dead', 400); }
        else if (toPin * 3 <= 15) { this.hud.callout(`STUCK IT · ${feet(toPin)} ft`, 'gold small'); this.award('Stuck it close', 250); }
        else this.hud.callout(`${feet(toPin)} ft to the hole`, 'small');
      } else if (surface === 'bunker') {
        this.hud.callout('BUNKER', 'bad');
      } else if (surface === 'rough') {
        this.hud.callout('ROUGH', 'bad small');
      } else if (surface === 'fringe') {
        this.hud.callout('FRINGE', 'small');
      } else if (surface === 'fairway' && shot.fromSurface !== 'tee') {
        this.hud.callout(`${Math.round(toPin)}y to go`, 'small');
      }
    }

    this.setState('settle');
    this.settleFor = 1.0;
    this.afterSettle = () => { if (!this.pickUpIfDone()) this.beginAim(); };
  }

  // ===========================================================================
  // Holing out
  // ===========================================================================

  holed(e) {
    const { shot, world, round } = this;
    const par = world.spec.par;
    this.holeLog.holed = true;
    this.net.holedOut();
    if (!shot.putt && this.holeLog.onGreenIn === null) { this.holeLog.onGreenIn = this.strokes; this.holeLog.firstProximity = 0; }
    this.effects.endTrail();
    this.hud.setCarry(null);
    this.audio.cup();
    this.music.accent();
    this.sink = 0.001;
    this.timeScale = 1;
    this.effects.confetti(world.cup.x, world.cup.y, world.cup.z, 70 + Math.max(0, par - this.strokes) * 50);
    this.rig.addShake(0.08);

    if (shot.knocked) {
      this.hud.callout(`KNOCKED IN! THANKS ${shot.by.toUpperCase()}`, 'gold');
    } else if (shot.putt && !shot.gimme) {
      const ft = feet(shot.toPin);
      round.stats.longestPutt = Math.max(round.stats.longestPutt, ft);
      if (ft >= 20) this.hud.callout(`${ft} FT BOMB!`, 'gold');
      this.award(`${ft} ft putt holed`, Math.min(600, 40 + ft * 14));
      if (this.putts === 1) this.award('One-putt', 150);
    } else if (!shot.putt) {
      if (this.strokes === 1) this.award('HOLE IN ONE', 10000);
      else if (shot.toPin > 60) this.award('Holed from the fairway', 3000);
      else this.award(e.dunk ? 'Slam dunk' : 'Chip-in', 1000);
      this.hud.callout(this.strokes === 1 ? 'ACE!!!' : 'HOLED IT!', 'gold');
    }

    const d = this.strokes - par;
    if (d < 0) this.dog.on('birdie');
    const scoreBonus = d <= -2 ? 1500 : d === -1 ? 600 : d === 0 ? 200 : d === 1 ? 50 : 0;
    if (scoreBonus) this.award(scoreName(this.strokes, par).replace('!', ''), scoreBonus);

    this.setState('holed');
    this.celebrateAngle = Math.atan2(this.camera.position.z - world.cup.z, this.camera.position.x - world.cup.x);
    this.announced = false;
  }

  /**
   * Yakizakana: the camera swings over to a fire by the green and the fish
   * caught on this hole goes on it. then() runs when it has been eaten (or
   * burnt). Free play (practice) passes no round and scores nothing.
   */
  startCooking(then) {
    const { world } = this;
    const spot = Grill.place(world, this.scenery.placed);
    const y = world.heightAt(spot.x, spot.z);
    // Camera stands on the green side of the fire, the skewer across its view
    const view = Math.atan2(world.cup.z - spot.z, world.cup.x - spot.x);
    this.cookView = { x: spot.x, y, z: spot.z, angle: view };
    this.cookThen = then;
    this.cookEnd = null;
    this.hud.setPlayVisible(false);
    this.hud.clearLayer();
    this.ballMesh.visible = this.blob.visible = false;
    this.setState('cook');
    this.grill.start({
      x: spot.x, y, z: spot.z,
      facing: Math.atan2(-Math.cos(view), -Math.sin(view)),
      level: this.assists?.level ?? 0.3,
    });
    this.campPose = 'warm';
    this.placeCamper(this.grill.group, 1.3, -0.35, Math.atan2(-1.3, 0.35), 1.6);
    this.cookHud.show(this.hints.cook < 3);
    this.cookHud.update(this.grill);
    this.audio.whoosh();
  }

  /** The EAT button: take the fish off the fire as it is. */
  eatFish() {
    if (this.state !== 'cook' || this.cookEnd !== null || this.stateTime < 1.2) return;
    this.cookResult(this.grill.take());
  }

  cookResult(grade) {
    this.cookEnd = this.stateTime;
    this.cookHud.finish();
    if (this.hints.cook < 3) { this.hints.cook += 1; store.set(HINT_KEY, this.hints); }
    const scoring = !!this.round;
    if (grade === 'perfect') {
      this.hud.callout('PERFECT YAKIZAKANA!', 'gold');
      if (scoring) { this.award('Perfect yakizakana', 600, true); this.earn('perfectGrill'); }
      this.audio.fanfare(2);
    } else if (grade === 'cooked') {
      this.hud.callout('TASTY!', '');
      if (scoring) this.award('Yakizakana', 250, true);
      this.audio.reward(3);
    } else if (grade === 'raw') {
      this.hud.callout('STILL RAW…', 'bad small');
      if (scoring) this.award('Raw fish', 30, true);
      this.audio.reward(0);
    } else {
      this.hud.callout('BURNT!', 'bad');
      this.hud.callout('NOTHING TO EAT', 'bad small');
      this.audio.penalty();
    }
  }

  finishHole() {
    if (!Number.isFinite(this.celebrateAngle)) this.celebrateAngle = 0; // picked up before ever holing out
    // A fish caught on this hole is grilled before the card comes up
    // (one fish a time, up to three, so a big haul does not stall the round)
    if (this.round.fish) {
      this.round.cooked = (this.round.cooked || 0) + 1;
      this.round.fish = this.round.cooked > 3 ? 0 : this.round.fish - 1;
      this.startCooking(() => this.finishHole());
      return;
    }
    this.round.cooked = 0;
    const { round, world } = this;
    const par = world.spec.par;
    const log = this.holeLog;
    log.strokes = this.strokes; log.putts = this.putts;
    this.earn('holes');
    if (this.net.active && this.net.humans.length) this.earn('friends');
    if (log.holed && this.strokes < par) this.earn('birdies');
    const won = !!this.challenge.test(log);
    if (won) {
      this.award(`★ ${this.challenge.text}`, this.challenge.points);
      round.stars = (round.stars || 0) + 1;
    }
    this.hud.setChallenge(this.challenge.text, won);
    round.heat = nextHeat(round.heat || 0, this.strokes, par);
    round.scores[round.index] = { number: world.spec.number, par, strokes: this.strokes, putts: this.putts, star: won };
    const last = round.index >= round.holes.length - 1;
    const card = this.cardData();
    round.index += 1;
    round.live = null;
    store.set(SAVE_KEY, round);

    const d = this.strokes - par;
    this.hud.setPlayVisible(false);
    this.updateScoreHud();
    this.net.holeDone(this.strokes, this.roundTotal(), { putts: this.putts, drive: log.longestDrive || 0, pin: log.teeToPin ?? null, near: log.firstProximity ?? null });
    this.setState('result');
    this.spectName = undefined;
    this.cardOpen = false;
    this.nextHole = () => {
      this.nextHole = null;
      this.audio.tap();
      this.hud.clearLayer();
      this.dip(() => { if (last) this.showSummary(); else this.startHole(); });
    };
    this.hud.showResult({
      title: scoreName(this.strokes, par),
      kind: d < 0 ? 'gold' : '',
      strokes: this.strokes, par,
      bonuses: this.bonuses,
      holePoints: this.holePoints,
      challenge: { text: this.challenge.text, won },
      card, last,
      board: this.net.active && this.net.players.length > 1 ? this.net.board() : null,
      cards: last ? null : this.net.dealHand(),
      acorns: this.net.acorns,
      onCard: (id) => this.net.keep(id),
      onReroll: () => this.net.reroll(),
      onNext: () => this.pressNext(),
      onShop: last ? null : () => this.openShop(),
      points: round.points,
      onToggle: () => { this.cardOpen = !this.cardOpen; this.hud.miniResult(!!this.spectName && !this.cardOpen, this.spectName); },
    });
  }

  saveCamp() { store.set(CAMP_KEY, this.camp); }

  /** Back from the bank to the ball. */
  endSkip() {
    if (this.state !== 'skip') return;
    this.setState('aim');
    this.hud.setControlsVisible(true);
    this.walkIn = { t: 0, dur: 1.0 };
    this.planDirty = true;
  }

  /** The hot pot on the campfire. back() returns to the summary card. */
  startNabe(back) {
    this.hud.clearLayer();
    this.camperUi.hidePoses();
    this.setState('nabe');
    this.setCampPose('warm');
    this.nabeBack = back;
    this.nabe.open(() => { this.setState('summary'); back(); });
    this.audio.whoosh();
  }

  eatNabe() {
    const res = this.nabe.eat();
    if (!res) return;
    this.nabe.close();
    this.round.nabeDone = true;
    const names = res.glow ? 'MOONLIGHT NABE' : res.gold ? 'GOLDEN NABE' : res.kinds >= 2 ? 'FOREST NABE' : 'CAMP NABE';
    const stars = '★'.repeat(res.stars) + '☆'.repeat(3 - res.stars);
    this.hud.callout(res.stars === 3 ? `PERFECT ${names}!` : names, res.stars === 3 ? 'gold' : '');
    this.audio.fanfare(res.stars);
    this.earn('nabe');
    this.dog.react('hop', 2.5);
    this.dog.hearts(4);
    this.setCampPose('cheer');
    const lines = [
      res.cooked ? `${res.cooked} things cooked through` : '',
      res.kinds ? `${res.kinds} kind${res.kinds > 1 ? 's' : ''} of wild mushroom` : 'Gather mushrooms on the course for a better pot',
      `At a simmer ${Math.round(res.simmer * 100)}% of the time`,
      this.nabe.boils > 1 ? `Boiled over ${this.nabe.boils} times` : this.nabe.boils ? 'Boiled over once' : 'Never boiled over',
    ].filter(Boolean);
    const node = document.createElement('div');
    node.className = 'overlay';
    node.innerHTML = `<div class="card nabe-card"><h2>${names}</h2><h1 class="gold stars">${stars}</h1>${lines.map((l) => `<p>${l}</p>`).join('')}<button class="btn" data-a="ok">BACK TO THE FIRE</button></div>`;
    node.querySelector('[data-a="ok"]').addEventListener('click', () => {
      this.audio.tap();
      this.setCampPose('sit');
      this.setState('summary');
      this.hud.clearLayer();
      this.nabeBack?.();
    });
    setTimeout(() => { if (this.state === 'nabe') { this.hud.clearLayer(); this.hud.layer.appendChild(node); } }, 1400);
  }

  showSummary() {
    const round = this.round;
    const total = this.roundTotal();
    const strokes = round.scores.reduce((s, h) => s + h.strokes, 0) - 2 * (round.gold || 0);
    const par = round.scores.reduce((s, h) => s + h.par, 0);
    const st = round.stats;
    let best = false;
    if (round.length === 18) {
      const prev = store.get(BEST_KEY);
      if (!prev || total < prev.score || (total === prev.score && round.points > prev.points)) {
        store.set(BEST_KEY, { score: total, points: round.points, seed: round.seed });
        best = true;
      }
    }
    if (round.daily) {
      const all = store.get(DAILY_KEY) || {};
      const prev = all[round.seed];
      if (!prev || total < prev.score) { all[round.seed] = { score: total, points: round.points }; best = true; }
      // Only today's (and a few recent) results are worth keeping
      for (const key of Object.keys(all).sort().slice(0, -7)) delete all[key];
      store.set(DAILY_KEY, all);
    }
    store.del(SAVE_KEY);
    if (!this.net.active) this.wakeLock?.release?.().catch(() => {}); // friends: stay up for the fire
    this.setState('summary');
    this.audio.fanfare(total <= 0 ? 3 : 1);
    this.earn('rounds');
    this.hud.setPlayVisible(false);
    this.pitchCamp();
    const card = this.cardData();
    card.holes.forEach((h) => { h.current = false; });
    const outfits = this.newOutfits;
    this.newOutfits = [];
    const showCard = () => this.hud.showSummary({
      total, par, strokes, points: round.points, best, seed: round.seed, card, outfits,
      board: this.net.active && this.net.players.length > 1 ? this.net.standings(total) : null,
      awards: this.net.active && this.net.players.length > 1 ? this.net.awards() : null,
      onCamp: () => this.campPoses(showCard),
      onNabe: round.nabeDone ? null : () => this.startNabe(showCard),
      stats: [
        { label: 'FAIRWAYS', value: `${st.fairways}/${st.fairwayChances}` },
        { label: 'GREENS IN REG', value: `${st.gir}/${round.holes.length}` },
        { label: 'PUTTS', value: st.putts },
        { label: 'PURE STRIKES', value: st.pures },
        { label: 'LONGEST DRIVE', value: `${st.longestDrive}y` },
        { label: 'CHALLENGES', value: `★ ${round.stars || 0}/${round.holes.length}` },
      ],
      onRoom: this.net.room ? () => {
        // Same room, same friends (and bots): the host starts the next round from the lobby
        this.hud.clearLayer();
        this.seed = generateSeed();
        this.showTitle();
        this.net.backToRoom();
      } : null,
      onAgain: () => {
        this.net.leave();
        this.seedPinned = false;
        this.seed = generateSeed();
        history.replaceState(null, '', location.pathname);
        this.hud.clearLayer();
        this.showTitle();
      },
      onShare: async (button) => {
        const url = `${location.origin}${location.pathname}?seed=${encodeURIComponent(round.seed)}`;
        const text = `I shot ${total === 0 ? 'even par' : total > 0 ? `+${total}` : total} on ThreeWood course ${round.seed}. Beat it:`;
        try {
          if (navigator.share) await navigator.share({ title: 'ThreeWood', text, url });
          else { await navigator.clipboard.writeText(`${text} ${url}`); button.textContent = 'LINK COPIED'; }
        } catch { /* cancelled */ }
      },
    });
    showCard();
    // Round the fire, each award over its winner's head in turn
    if (this.net.active && this.net.players.length > 1) {
      this.net.awards().forEach((a, i) => setTimeout(() => {
        if (this.state !== 'summary') return;
        if (a.me) { this.camper.feel('joy', 2.5); this.hud.callout(`${a.e} ${a.label.toUpperCase()}`, 'small'); } // (not gold: that is shared, and every phone pops its own)
        else { this.net.others.say(a.id, `${a.e} ${a.label}`); this.net.others.react(a.id, 'joy'); }
        this.audio.reward(i);
      }, 1800 + i * 1600));
    }
  }

  endIntro() {
    this.hud.clearLayer();
    this.audio.tap();
    this.beginAim();
  }

  // ===========================================================================
  // Menu
  // ===========================================================================

  openMenu() {
    if (!this.round || this.paused || this.state === 'result' || this.state === 'summary' || this.state === 'fishing' || this.state === 'cook') return;
    this.paused = true;
    this.audio.tap();
    this.hud.showMenu({
      muted: this.audio.muted,
      music: this.music.enabled,
      card: this.cardData(),
      onResume: () => this.closeMenu(),
      onMute: () => { this.audio.setMuted(!this.audio.muted); return this.audio.muted; },
      sky: this.sky.mode,
      onSky: () => this.sky.cycleMode(),
      steady: this.steady,
      onSteady: () => { this.steady = !this.steady; store.set(STEADY_KEY, this.steady); return this.steady; },
      onMusic: () => this.toggleMusic(),
      onHelp: () => this.hud.showHelp(() => this.openMenuAgain()),
      onFishing: () => this.practiceFishing(() => this.openMenuAgain()),
      onQuit: () => { this.paused = false; this.net.leave(); this.hud.clearLayer(); this.showTitle(); },
    });
  }

  toggleMusic() {
    this.audio.unlock();
    return this.music.setEnabled(!this.music.enabled);
  }

  openMenuAgain() { this.paused = false; this.openMenu(); }

  /**
   * Practice fishing: the three games in turn, for as long as you like, with
   * nothing at stake. Runs on its own clock so it works from the title and
   * from the pause menu, where the game itself is standing still.
   */
  practiceFishing(onClose) {
    const a = this.audio;
    const modes = ['drop', 'steer', 'gold'];
    let i = 0, last = performance.now(), open = true;
    this.hud.clearLayer();
    const next = () => this.fishing.start({
      mode: modes[i++ % modes.length],
      level: 0.3,
      color: this.world.biome.liquid === 'lava' ? 0x3c9cc4 : this.world.biome.water,
      hint: true,
      sounds: {
        drop: () => a.whoosh(), hooked: () => a.tap(), bump: () => a.bounce(6, 'rough'),
        catch: () => a.reward(4), fish: () => a.reward(0), miss: () => a.penalty(),
      },
      onDone: next,
      onClose: () => { open = false; onClose(); },
    });
    next();
    const tick = (now) => {
      if (!open) return;
      this.fishing.update(Math.min(0.05, (now - last) / 1000));
      last = now;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  closeMenu() {
    this.paused = false;
    this.hud.clearLayer();
    if (this.state === 'intro') {
      // The menu replaced the intro card: put it back
      this.hud.showIntro(this.introCard());
    }
  }

  // ===========================================================================
  // Frame
  // ===========================================================================

  loop() {
    requestAnimationFrame(this.loop);
    if (this.manual) return;
    const raw = this.clock.getDelta();
    const dt = Math.min(raw, 0.05);
    this.adaptQuality(raw);
    this.music.setMuffled(this.paused);
    if (!this.paused) this.update(dt);
    this.updateVisuals(dt);
    this.renderer.render(this.scene, this.camera);
    this.photoFrame(dt);
  }

  update(dt) {
    this.time += dt;
    this.stateTime += dt;
    const { ball, world } = this;

    switch (this.state) {
      case 'creator': {
        this.frameCreator(dt);
        break;
      }
      case 'summary': {
        const v = this.campView;
        if (v) {
          // For the photo: a little wider and higher, with the friends in frame
          if (this.photo) this.photoFrameCamp(v);
          else this.rig.orbit(v.x, v.y + 0.95, v.z, 4.3, 0.42, v.angle + Math.sin(this.time * 0.25) * 0.22);
          this.grill.update(dt);
        }
        break;
      }
      case 'title': {
        // A slow turn round your camper and Kuri on the first green, low
        // enough to see the course behind them
        const t = this.titleSpot;
        const ang = t.angle + Math.sin(this.time * 0.11) * 0.6;
        if (this.rig.portrait) {
          // Frame the camper and Kuri in the gap the menu actually leaves:
          // between the bottom of the logo and the top of the first button,
          // whatever the phone's shape and however many buttons there are
          const band = this.titleBand();
          const H = window.innerHeight;
          const tanHalf = Math.tan((this.camera.fov * Math.PI) / 360);
          // Far enough for the pair to fit the gap's height, and the screen's width
          const tall = (2.5 * H) / (Math.max(80, band.h) * 2 * tanHalf);
          const wide = 1.75 / (tanHalf * (window.innerWidth / H));
          const d = Math.max(4.5, Math.min(13, Math.max(tall, wide)));
          this.rig.orbit(t.x, t.y + 0.55, t.z, d, 0.45, ang);
          // Slide the picture so the middle of the camper sits in the middle of the gap
          this.titleShift = (this.titleShift ?? 0) + ((H / 2 - band.mid) - (this.titleShift ?? 0)) * Math.min(1, dt * 6);
          this.camera.setViewOffset(window.innerWidth, H, 0, this.titleShift, window.innerWidth, H);
        } else {
          // Landscape: the menu is on the right, so frame the pair left of centre
          const side = -1.9, sx = -Math.sin(ang) * side, sz = Math.cos(ang) * side;
          this.rig.orbit(t.x + sx, t.y + 0.68, t.z + sz, 6.4, 0.75, ang);
        }
        break;
      }
      case 'intro': {
        this.rig.flyover(Math.min(1, this.stateTime / (INTRO_TIME - 0.6)));
        if (this.stateTime > INTRO_TIME) this.endIntro();
        break;
      }
      case 'aim': {
        // (the aim has settled after a swing round: work the power out again for the new line)
        if (this.calStale && performance.now() - this.cal.at > 220) { this.cal.at = 0; this.planDirty = true; }
        // Keys on a PC: A D (or ← →) to aim, W S for distance (pace on a putt).
        // Held longer, the turn speeds up; Shift for the fine adjustments.
        const k = this.keys;
        const turn = (k.ArrowRight || k.KeyD ? 1 : 0) - (k.ArrowLeft || k.KeyA ? 1 : 0);
        this.turnHeld = turn ? (this.turnHeld || 0) + dt : 0;
        if (turn) {
          const ramp = Math.min(3, 1 + this.turnHeld * 1.5) * (k.ShiftLeft || k.ShiftRight ? 0.25 : 1);
          this.aimAngle += turn * dt * (this.putting ? 0.12 : 0.7) * ramp * (this.mods?.backwards ? -1 : 1);
          this.planDirty = true;
        }
        const reach = (k.KeyW ? 1 : 0) - (k.KeyS ? 1 : 0);
        if (reach) this.adjustLength(-reach * dt * (this.putting ? 90 : 220) * (k.ShiftLeft || k.ShiftRight ? 0.3 : 1));
        if (this.planDirty) this.updatePlan();
        this.aimCamera();
        break;
      }
      case 'swing': {
        this.aimCamera();
        if (this.putting) {
          if (this.charging) {
            const rate = PUTT_METER_RATE * (this.coach ? (this.coach === 'hold' ? 0.7 : this.coach) : this.assists.puttRate) * (this.steady ? 0.65 : 1);
            const pausing = this.coach === 'hold';
            this.puttPower += this.puttDir * rate * dt;
            if (pausing && this.puttDir > 0 && this.puttPower >= this.idealPct) {
              // Coached first putt (or steady mode): the bar stops on the mark and waits
              this.puttPower = this.idealPct;
              this.dwell += dt;
              if (!this.coachHeld) {
                this.coachHeld = true;
                this.hud.setMeterFlag(this.idealPct, this.puttLatched ? 'tap' : 'release', true);
              }
            }
            if (this.puttPower >= 100 && this.puttDir > 0) {
              // Rest at the top for a moment: letting go late is still full pace
              this.puttPower = 100;
              this.puttHold = (this.puttHold || 0) + dt;
              if (this.puttHold > 0.4) { this.puttDir = -1; this.puttHold = 0; }
            }
            if (this.puttPower <= 0) { this.puttPower = 0; this.puttDir = 1; }
            this.hud.updatePuttMeter(this.puttPower);
          }
        } else {
          this.lockGrace = Math.max(0, (this.lockGrace || 0) - dt);
          const sw = this.swing;
          let step = dt * (this.coach ? (this.coach === 'hold' ? 0.75 : this.coach) : this.assists.tempo) * (this.steady ? 0.7 : 1);
          if (this.coach === 'hold' || this.steady) {
            // Coached first swing: the marker stops where the tap belongs and
            // waits. Steady mode: it pauses on the strike line for half a second
            const atPower = this.coach === 'hold' && sw.phase === 'power' && this.idealPower && sw.marker >= this.idealPower;
            const atLine = sw.phase === 'accuracy' && sw.marker <= SWING.LINE + 0.6 && sw.marker > SWING.LINE - 2;
            if ((atPower || atLine) && (this.coach === 'hold' || this.dwell < 0.55)) {
              step = 0;
              sw.marker = atPower ? this.idealPower : SWING.LINE;
              this.dwell += dt;
              if (this.coachHeld !== sw.phase) {
                this.coachHeld = sw.phase;
                this.dwell = 0;
                this.hud.setMeterFlag(markerToPercent(sw.marker), 'tap', true);
              }
            }
          }
          const event = swingStep(this.swing, step);
          this.hud.updateSwingMeter(this.swing);
          if (event) this.handleSwingEvent(event);
        }
        break;
      }
      case 'flight':
        this.updateFlight(dt);
        break;
      case 'fishing':
        this.fishing.update(dt);
        break;
      case 'nabe': {
        const v = this.campView;
        this.rig.orbit(v.x, v.y + 0.25, v.z, 1.75, 1.45, v.angle + 0.35);
        this.grill.update(dt);
        break;
      }
      case 'cook': {
        const v = this.cookView;
        // Wide enough for the whole skewer, the camper tending it beside the fire
        this.rig.orbit(v.x, v.y + 0.6, v.z, 3.3, 1.35, v.angle - 0.85);
        // Nothing cooks until the camera has arrived
        if (this.stateTime > 1.2) {
          if (this.grill.update(dt) && this.cookEnd === null) this.cookResult('burnt');
        } else {
          this.grill.paint();
        }
        this.cookHud.update(this.grill);
        if (this.cookEnd !== null && this.stateTime - this.cookEnd > 1.8) {
          const then = this.cookThen;
          this.cookThen = null;
          this.setState('settle'); // puts the fire away
          then?.();
        }
        break;
      }
      case 'settle':
        if (this.shot?.putt) this.rig.watch(ball); else if (this.rig.mode === 'landing') this.rig.landing(ball, this.plan, this.shot.fromX, this.shot.fromZ);
        if (this.stateTime > this.settleFor) this.afterSettle();
        break;
      case 'holed':
        // A gentle drift, not a full orbit: the scene stays composed
        this.celebrateAngle += dt * 0.12;
        this.rig.orbit(world.cup.x, world.cup.y + 1.15, world.cup.z, 5.0, 0.9, this.celebrateAngle);
        if (!this.announced && this.stateTime > 0.45) {
          this.announced = true;
          const d = this.strokes - world.spec.par;
          this.hud.callout(scoreName(this.strokes, world.spec.par), d < 0 ? 'gold' : d > 0 ? 'bad' : '');
          this.audio.fanfare(d <= -2 ? 3 : d === -1 ? 2 : d === 0 ? 1 : 0);
        }
        if (this.stateTime > 2.6) this.finishHole();
        break;
      case 'result': {
        // With friends still out there, the wait is a show: the card tucks
        // itself away and the camera goes to whoever is still playing
        const watching = this.net.active && this.stateTime > 2.5 ? this.net.spectate(dt) : null;
        if (watching !== this.spectName) { this.spectName = watching; this.hud.miniResult(!!watching && !this.cardOpen, watching); }
        if (!watching) {
          this.celebrateAngle += dt * 0.08;
          this.rig.orbit(world.cup.x, world.cup.y, world.cup.z, 9, 4, this.celebrateAngle);
        }
        this.hud.setAutoProgress(this.stateTime / 9);
        if (this.stateTime > 9 && !this.net.active) this.nextHole?.();
        break;
      }
      default:
        break;
    }
  }

  aimCamera() {
    const { ball } = this;
    const dirX = Math.cos(this.aimAngle), dirZ = Math.sin(this.aimAngle);
    // Setting the length: pull up and back so the whole path is on screen
    if (this.state === 'aim' && this.previewUntil > performance.now() && this.previewPts?.length >= 6) {
      this.rig.frame(ball, dirX, dirZ, this.previewPts);
      return;
    }
    // Ready on the tee, waiting for the others: the whole tee in the picture, to watch everyone hit
    // (and it stays there through my swing: the meter is on screen, and the
    // whole tee swinging at once is the moment worth seeing)
    const tee = this.onTee && this.net.active && this.net.players.length > 1 && !this.putting;
    if (tee && ((this.state === 'aim' && this.net.status.get(this.net.myId) === 'ready') || this.state === 'swing')) {
      const spots = this.net.teeSpots();
      if (spots.length) { this.rig.gallery(ball, dirX, dirZ, spots); return; }
    }
    // Crazy golf: from above, to line up the rebounds
    if (this.putting && this.world.lane && this.previewPts?.length >= 6) { this.rig.birdseye(ball, dirX, dirZ, this.previewPts); return; }
    if (this.putting) {
      this.rig.putt(ball, dirX, dirZ, this.targetDist);
    } else {
      const reach = this.idealPower ? this.targetDist : distanceAt(this.club, 100, this.lie.speedFactor);
      this.rig.aim(ball, dirX, dirZ, reach);
    }
  }

  updateVisuals(dt) {
    const { ball, world, camera } = this;
    // Watching a friend's ball overrides whatever camera the state wanted
    this.net.watchCamera();
    this.rig.update(dt);

    // Ball: drawn a little larger with distance so it never becomes a pixel
    const camDist = Math.hypot(camera.position.x - ball.x, camera.position.y - ball.y, camera.position.z - ball.z);
    // (only enough to stay visible: beyond that it must grow and shrink with
    // distance like a real thing, or a ball flying at the camera looks wrong)
    // (no extra growth in the air: with the camera holding still while the
    // ball flies away, that made it balloon next to the scenery; a faint halo
    // does the job of keeping it readable instead)
    const air = this.state === 'flight' && ball.mode === 'air';
    // (and never past 4×: from a camera parked behind the golfer, a ball 150
    // yards out drawn 12× was a beach ball on the green. The halo carries it.)
    const want = Math.max(1.4, Math.min(4, camDist / (this.rig.portrait ? 8.5 : 11)));
    // Shrink at once (camera cuts), grow smoothly (ball flying away)
    this.ballScale = want < this.ballScale ? want : this.ballScale + (want - this.ballScale) * Math.min(1, dt * 8);
    const s = this.ballScale;
    const ground = world.heightAt(ball.x, ball.z);
    const lift = Math.max(0, ball.y - BALL_R - ground);
    // Centred on the real ball in the air; resting on the turf when it is down
    let y = Math.max(ball.y, ground + BALL_R * s);
    if (this.sink > 0) {
      this.sink = Math.min(1, this.sink + dt * 3.2);
      y -= this.sink * 0.42;
      this.ballMesh.position.x += (world.cup.x - this.ballMesh.position.x) * 0.3;
      this.ballMesh.position.z += (world.cup.z - this.ballMesh.position.z) * 0.3;
      this.ballMesh.position.y = y;
    } else {
      this.ballMesh.position.set(ball.x, y, ball.z);
    }
    this.ballMesh.scale.setScalar(s);
    this.ballGlowK = (this.ballGlowK || 0) + ((air ? 1 : 0) - (this.ballGlowK || 0)) * Math.min(1, dt * 6);
    this.ballGlow.visible = this.ballGlowK > 0.02 && this.ballMesh.visible;
    this.ballGlow.position.copy(this.ballMesh.position);
    this.ballGlow.scale.setScalar(Math.max(BALL_R * s * 5, camDist * 0.012));
    this.ballGlow.material.opacity = this.ballGlowK * Math.min(0.7, 0.45 + camDist * 0.002);
    if (ball.mode === 'roll' || ball.mode === 'air') {
      const speed = Math.hypot(ball.vx, ball.vz);
      if (speed > 0.05) {
        this._axis = this._axis || new THREE.Vector3();
        this._axis.set(ball.vz, 0, -ball.vx).normalize();
        this.ballMesh.rotateOnWorldAxis(this._axis, (speed * dt * this.timeScale) / (BALL_R * s));
      }
    }
    // Trail: laid from where the ball is drawn, so it can never come adrift
    const inFlight = this.state === 'flight' && this.shot && !this.shot.putt;
    const pace = Math.hypot(ball.vx, ball.vy, ball.vz);
    const airborne = ball.mode === 'air';
    this.effects.comet.update(
      this.ballMesh.position, this.time, camera, BALL_R * s,
      airborne ? 0.6 : 0.28,
      inFlight ? (airborne ? 1 : Math.min(1, Math.max(0, (pace - 2) / 8))) : 0);
    this.blob.position.set(this.ballMesh.position.x, ground + 0.03, this.ballMesh.position.z);
    this.blob.scale.setScalar(BALL_R * s * (1.25 + lift * 0.04));
    this.blob.material.opacity = this.sink > 0 ? 0 : Math.max(0.1, 0.34 - lift * 0.006);

    // Sun follows the action so one shadow map covers it
    const fx = Math.round(this.rig.look.x / 8) * 8, fz = Math.round(this.rig.look.z / 8) * 8;
    this.sun.target.position.set(fx, 0, fz);
    this.sky.update(dt, this.time);
    const light = this.sky.lightDir;
    this.sun.position.set(fx + light.x * 260, light.y * 260, fz + light.z * 260);

    this.updateClub(dt);
    if (this.dressing) {
      this.dressing.visible = this.state !== 'title' && this.state !== 'creator'; // no beacon behind the logo, or in front of the camper
      // The bucket's beam finds the cup from the tee; up close it would fill the screen
      const beacon = this.dressing.userData.beacon;
      if (beacon) {
        const d = Math.hypot(camera.position.x - beacon.position.x, camera.position.z - beacon.position.z);
        beacon.material.opacity = 0.42 * Math.min(1, Math.max(0, (d - 10) / 30));
        beacon.visible = beacon.material.opacity > 0.01;
      }
    }
    this.updateCamper(dt);
    this.dog.update(dt);
    this.forage.update(dt);
    this.skip.update(dt);
    this.nabe.update(dt);
    this.net.update(dt);
    // The course's own sound: quieter under menus and minigames
    const busy = this.paused || ['fishing', 'cook', 'nabe', 'creator'].includes(this.state);
    this.audio.ambience(dt, { night: this.sky.night, wind: this.world?.spec.wind.speed || 0, level: busy ? 0.15 : this.state === 'title' ? 0.6 : 1 });
    updateScenery(this.scenery, this.time, dt, this.sky);
    updateCave(this.cave, this.time);
    if (this.myCloud) { const p = this.camper.group.position; this.myCloud.visible = this.camper.group.visible; updateRainCloud(this.myCloud, dt, p.x, p.y, p.z, this.camper.group.scale.x, this.time, this.camera); }
    // After dark: the course, the ball and the campers carry their own light
    const night = this.sky.night;
    updateNightGlow(this.nightGlow, night, this.time, camera);
    this.gimmicks.update(dt, this.time);
    this.ballMesh.material.emissiveIntensity = 0.42 + night * 0.9;
    // (gentler on the green, where the camera is close and it would bleach the turf)
    this.ballLight.intensity = this.ballMesh.visible ? night * (this.putting || ball.surface === 'green' ? 1.6 : 7) : 0;
    this.ballLight.position.set(this.ballMesh.position.x, this.ballMesh.position.y + 0.7, this.ballMesh.position.z);
    this.camper.setGlow(night);
    for (const o of this.net.others.map.values()) o.camper.setGlow(night);
    const onGreen = this.round && (this.putting || ball.surface === 'green') && this.state !== 'intro' && this.state !== 'title';
    updateFlag(this.scenery.flag, this.time, onGreen && this.state !== 'holed' && this.state !== 'result', camera);
    // Trying on outfits, nothing of the course may stand in front of the camper
    // (an upright phone pulls the camera back past the pin: the flagpole filled the view)
    const creator = this.state === 'creator';
    this.scenery.flag.visible = !creator;
    if (creator && this.nightGlow) this.nightGlow.visible = false;
    this.effects.update(dt, this.time);

    if (!this.round) return;

    // HUD that tracks the 3D scene
    const showTag = (this.state === 'aim' || this.state === 'swing') && !this.putting;
    if (showTag) {
      this._v = this._v || new THREE.Vector3();
      this._v.set(world.cup.x, world.cup.y + 4.1, world.cup.z).project(camera);
      const visible = this._v.z < 1 && Math.abs(this._v.x) < 1.1;
      const d = Math.hypot(world.cup.x - ball.x, world.cup.z - ball.z);
      this.hud.setPinTag(`${Math.round(d)}y`, (this._v.x * 0.5 + 0.5) * window.innerWidth, (-this._v.y * 0.5 + 0.5) * window.innerHeight, visible);
    } else {
      this.hud.setPinTag('', 0, 0, false);
    }
    if (showTag && this.landing) {
      // Where the shot finishes (ball at rest), with how far that is
      // (locked on a mushroom: the tag is on its cap, and says so)
      const lp = this.locked?.pad;
      const at = lp || this.rest || this.landing;
      this._v2 = this._v2 || new THREE.Vector3();
      this._v2.set(at.x, lp ? lp.top + 0.3 : world.heightAt(at.x, at.z) + 0.2, at.z).project(camera);
      const visible = this._v2.z < 1 && Math.abs(this._v2.x) < 1.05 && Math.abs(this._v2.y) < 1.05;
      const d = Math.hypot(at.x - ball.x, at.z - ball.z);
      this.hud.setLandTag(lp ? `🍄 BOING · ${Math.round(d)}y` : `${Math.round(d)}y`, (this._v2.x * 0.5 + 0.5) * window.innerWidth, (-this._v2.y * 0.5 + 0.5) * window.innerHeight, visible);
    } else {
      this.hud.setLandTag('', 0, 0, false);
    }

    if (this.state === 'aim' || this.state === 'swing' || this.state === 'flight') {
      // Wind arrow in screen space: up = away from the camera
      const dirX = Math.cos(this.aimAngle), dirZ = Math.sin(this.aimAngle);
      const w = this.env;
      const forward = w.windX * dirX + w.windZ * dirZ;
      const right = w.windX * -dirZ + w.windZ * dirX;
      this.hud.setWind(Math.round(Math.hypot(w.windX, w.windZ)), Math.atan2(right, forward));
      this.mapTimer = (this.mapTimer || 0) - dt;
      if (this.mapTimer <= 0) {
        this.mapTimer = 0.08;
        this.hud.drawMinimap(world, ball, this.state === 'flight' ? null : this.landing);
      }
    }
  }

  /** Test/capture hook: with `manual` set, advance exactly one frame. */
  debugFrame(dt, render = true) {
    if (!this.paused) this.update(dt);
    this.updateVisuals(dt);
    if (render) this.renderer.render(this.scene, this.camera);
  }

  /** Test hook: jump to hole `index` (0-based) of the round. */
  debugGoto(index) {
    this.round.index = index;
    this.hud.clearLayer();
    this.startHole();
  }

  /** Test hook: rebuild the current hole in another world (a biome id). */
  debugBiome(biome) {
    const spec = designHole(this.world.spec.seed, this.world.spec.number, { biome });
    this.loadWorld(spec);
    placeBall(this.ball, this.world, this.world.tee.x, this.world.tee.z);
    this.aimAngle = Math.atan2(this.world.cup.z - this.ball.z, this.world.cup.x - this.ball.x);
    if (this.state === 'aim') this.beginAim();
  }

  /** Test hook: drop the ball `dist` yards from the pin and take aim. */
  debugPlace(dist, angle = 0.6) {
    const { world, ball } = this;
    this.hud.clearLayer();
    this.hud.setPlayVisible(true);
    this.strokes = Math.max(1, this.strokes);
    placeBall(ball, world, world.cup.x + Math.cos(angle) * dist, world.cup.z + Math.sin(angle) * dist);
    this.ballMesh.visible = this.blob.visible = true;
    this.beginAim();
  }

  /** Drop resolution if the device cannot hold frame rate. */
  adaptQuality(dt) {
    this.frameTimes.push(dt);
    if (this.frameTimes.length < 90) return;
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    this.frameTimes.length = 0;
    if (avg <= 1 / 42) return;
    if (this.pixelRatio > 1) {
      this.pixelRatio = Math.max(1, this.pixelRatio - 0.25);
      this.renderer.setPixelRatio(this.pixelRatio);
      this.resize();
    } else if (this.sun.castShadow) {
      // Last resort: real shadows go, the ball's blob shadow stays
      this.sun.castShadow = false;
      this.renderer.shadowMap.needsUpdate = true;
    }
  }
}
